import { courseGenerationService } from '@/services/course-generation.service';
import { courseService } from '@/services/course.service';
import { documentStoreService } from '@/services/document-store.service';
import { languageConfigService } from '@/services/language-config.service';
import { languageOptions, getVoiceTypesForLanguage, getDefaultVoiceType, isVoiceTypeAvailable, isVoiceTypePitchSupported, getVoicesForLanguageAndType, formatVoiceLabel, getPreviewText, type VoiceType } from '@/constants/languageOptions';
import {
    CourseOrchestration,
    DifficultyLevel,
    Tone
} from '@/types/orchestration.types';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    AlertCircle,
    ArrowRight,
    BookOpen,
    CheckCircle,
    ChevronDown,
    ChevronUp,
    Clock,
    FileText,
    Image,
    Languages,
    Loader2,
    Settings,
    Sparkles,
    User,
    Video,
    Volume2,
    Wand2,
    XCircle,
    X,
    Target,
    Building,
    Clipboard,
    Undo2,
    RefreshCw,
    Search,
    Shirt,
    ShieldCheck,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { TagsField } from '@/components/common/form-fields';
import { Toggle } from '@/components/ui/Toggle';
import Pagination from '@/components/ui/Pagination';
import KnowledgeStoreAnalysisPanel from '@/components/courses/KnowledgeStoreAnalysisPanel';
import type { SuggestedStructureRange } from '@/types/document-store.types';
import { avatarModelsService, type AvatarModel, type AvatarVoice, type AvatarLook } from '@/services/avatar-models.service';
import ReactSelect, { StylesConfig } from 'react-select';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_GENERATION_PERMISSION } from '@/constants/constant-permissions';

// Map our locale codes to HeyGen language names for voice filtering
const LOCALE_TO_HEYGEN_LANGUAGE: Record<string, string> = {
  'en-US': 'English', 'en-GB': 'English', 'en-AU': 'English', 'en-IN': 'English',
  'hi-IN': 'Hindi', 'bn-IN': 'Bengali', 'te-IN': 'Telugu', 'mr-IN': 'Marathi',
  'ta-IN': 'Tamil', 'gu-IN': 'Gujarati', 'kn-IN': 'Kannada', 'ml-IN': 'Malayalam',
  'pa-IN': 'Punjabi', 'ur-IN': 'Urdu',
  'es-ES': 'Spanish', 'fr-FR': 'French', 'de-DE': 'German', 'pt-BR': 'Portuguese',
  'it-IT': 'Italian', 'zh-CN': 'Chinese', 'ja-JP': 'Japanese', 'ko-KR': 'Korean',
  'ar-SA': 'Arabic',
};

// Compact styles for react-select in avatar config
type VoiceSelectOption = { label: string; value: string; voice: AvatarVoice };
const voiceSelectStyles: StylesConfig<VoiceSelectOption, false> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? 'rgb(var(--brand-purple-500))' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgb(var(--brand-purple-500) / 0.15)' : 'none',
    '&:hover': { borderColor: 'rgb(var(--brand-purple-500))' },
    minHeight: '32px',
    borderRadius: '0.5rem',
    fontSize: '0.75rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'rgb(var(--brand-purple-500))' : state.isFocused ? 'rgb(var(--brand-purple-100))' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '6px 10px',
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
    zIndex: 50,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  placeholder: (provided) => ({ ...provided, color: '#9ca3af', fontSize: '0.75rem' }),
  singleValue: (provided) => ({ ...provided, color: '#1f2937', fontSize: '0.75rem' }),
  input: (provided) => ({ ...provided, fontSize: '0.75rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided) => ({ ...provided, padding: '4px' }),
};

// Form data type
type CourseFormData = {
  courseIdea: string;
  targetAudience: string;
  difficultyLevel: DifficultyLevel;
  tone: Tone;
  knowledgeStoreId: string;
  knowledgeStoreOnlyMode: boolean;
  exampleInjectionEnabled: boolean;
  prerequisites: string[];
  totalDuration: number;
  industry: string;
  pedagogyFramework: 'DEFAULT' | 'ADDIE' | 'BLOOMS_TAXONOMY' | 'GAGNE' | 'CONSTRUCTIVIST' | 'EXPERIENTIAL';
  languageCode: string;

  /**
   * Languages to ALSO produce, translated from the master after it generates.
   * The master publishes as soon as it is ready; these follow one at a time.
   */
  translationLanguages: Array<{ language_code: string; language_name: string }>;

  /**
   * Transient: the Select's current value. Cleared the moment the choice is
   * turned into a chip, so the control always reads "Add a language" and can be
   * used repeatedly. Never sent to the API.
   */
  translationPicker: string;

  deviceType: 'mobile' | 'desktop' | 'tablet' | 'speaker';
  ttsConfig: {
    voiceType: VoiceType;
    voiceName: string;
    speakingRate: number;
    pitch: number;
    volumeGainDb: number;
  };
  captionConfig: {
    enableCaptions: boolean;
    format: 'webvtt' | 'srt';
    maxDuration: number;
    minDuration: number;
  };
  htmlSlidesConfig: {
    contentFormat: 'html' | 'plain' | 'audio-friendly' | 'html-slides';
    theme: 'educational' | 'modern' | 'corporate' | 'dark' | 'minimal';
    videoRenderingMode: 'animated' | 'static';
    contentTypes: string[];
  };
  moduleGenerationConfig: {
    minModules: number;
    maxModules: number;
  };
  contentGenerationConfig: {
    minContentItemsPerModule: number;
    maxContentItemsPerModule: number;
  };
  sceneGenerationConfig: {
    minScenesPerContent: number;
    maxScenesPerContent: number;
  };
  contentDeliveryType: 'html' | 'html_with_audio' | 'video_presentation' | 'avatar_video';
  avatarConfig: {
    provider: 'd_id' | 'heygen';
    videoMode: 'talking_head' | 'full_animated_video';
    compositionMode: 'overlay' | 'fullscreen';
    sourceUrl: string;
    avatarId: string;
    voiceId: string;
    useVideoAgent: boolean;
    resolution: '720p' | '1080p' | '4k';
    engine: 'avatar_3' | 'avatar_4';
    targetDurationSeconds: number; // Video Agent (full_animated_video) only — 60-360s, default 180s
    position: 'bottom_right' | 'bottom_left' | 'top_right' | 'top_left';
    pipSizePercent: number;
    pipShape: 'circle' | 'rounded_rect';
    pipBorder: boolean;
    backgroundColor: string;
  };
  enableEnhancedFeatures: boolean;
  enhancedFeatures: {
    autoGrading: boolean;
    selfCheckQuizzes: boolean;
    adaptiveLearning: boolean;
    analytics: boolean;
    collaborative: boolean;
    microlearning: boolean;
  };
  disableAssessments: boolean;
  assessmentConfig: {
    enabled: boolean;
    questionTypes: ('SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'TEXT_ANSWER')[];
    contentItemAssessments: {
      generatePre: boolean;
      generatePost: boolean;
      minQuestions: number;
      maxQuestions: number;
      passingScore: number;
      timeLimitMinutes: number;
    };
    moduleAssessments: {
      generatePre: boolean;
      generatePost: boolean;
      minQuestions: number;
      maxQuestions: number;
      passingScore: number;
      timeLimitMinutes: number;
    };
    courseAssessments: {
      generatePre: boolean;
      generatePost: boolean;
      minQuestions: number;
      maxQuestions: number;
      passingScore: number;
      timeLimitMinutes: number;
    };
    immediateFeedback: boolean;
    allowRetry: boolean;
    maxAttempts: number;
    shuffleQuestions: boolean;
    shuffleOptions: boolean;
    showCorrectAnswers: boolean;
  };
};

// The validation phase (course_validation_status) shipped on 2026-06-11. Courses created
// before that have no such node — but the (non-lean) list API hydrates the missing field to
// { status: 'NOT_STARTED' }, so it's byte-identical to a new course that hasn't validated yet.
// The only frontend-reliable signal is createdAt: gate the stage on the ship date so it shows
// for new courses (even while NOT_STARTED) and never for old ones.
const VALIDATION_STAGE_SINCE = new Date('2026-06-11T00:00:00Z');

const hasValidationStage = (o: CourseOrchestration): boolean =>
  !!o.createdAt && new Date(o.createdAt) >= VALIDATION_STAGE_SINCE;
export default function AddCourse() {
  // Permission guard: either read OR write grants page access
  usePermissionRouteAccess([
    COURSE_GENERATION_PERMISSION.COURSE_GENERATION_READ,
    COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE,
  ]);
  // Action control: only write permission enables Enhance with AI / Generate Course
  const canWrite = useHasPermission([COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE]);
  // The suggestion the panel last loaded, so this page can tell whether the controls match it.
  // Null until the analysis arrives, and null again for a store that could not be analysed.
  const [analyzedSuggestion, setAnalyzedSuggestion] = useState<SuggestedStructureRange | null>(null);
  const [showAiWarning, setShowAiWarning] = useState(false);
  // The validated payload held back by that confirmation, so confirming generates the same
  // form data that was submitted rather than re-reading the form.
  const pendingGenerationRef = useRef<CourseFormData | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const trendingTopic = location.state?.trendingTopic;
  const ticketDescription = location.state?.ticketDescription;
  const ticketLanguageCode = location.state?.languageCode;
  const courseGenerationConfig = location.state?.courseGenerationConfig;

  // Resolve initial languageCode from URL ?language= param (set by the
  // LP "Build Course" suggestion flow). Lenient matching: exact first,
  // then prefix ('hi' → 'hi-IN'). Falls back to 'en-US'. Doing this at
  // form-init time (not via setValue in useEffect) so the Language
  // Settings picker reflects the right language on first render.
  const initial_language_code = (() => {
    const raw = searchParams.get('language') || ticketLanguageCode;
    if (!raw) return 'en-US';
    const lc = raw.toLowerCase();
    const exact = languageOptions.find((o) => o.value.toLowerCase() === lc);
    if (exact) return exact.value;
    const prefix = languageOptions.find(
      (o) => o.value.toLowerCase().split('-')[0] === lc.split('-')[0]
    );
    return prefix?.value || 'en-US';
  })();

  const [isGenerating, setIsGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [avatarModels, setAvatarModels] = useState<AvatarModel[]>([]);
  const [isLoadingAvatars, setIsLoadingAvatars] = useState(false);
  const [configuredProviders, setConfiguredProviders] = useState<{ d_id: boolean; heygen: boolean }>({ d_id: false, heygen: false });
  const [avatarVoices, setAvatarVoices] = useState<AvatarVoice[]>([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [avatarNextToken, setAvatarNextToken] = useState<string | null>(null);
  const [avatarHasMore, setAvatarHasMore] = useState(false);
  const [isLoadingMoreAvatars, setIsLoadingMoreAvatars] = useState(false);
  const [avatarSearch, setAvatarSearch] = useState('');
  const [avatarSourceFilter, setAvatarSourceFilter] = useState<'all' | 'heygen_stock' | 'private_upload'>('all');
  const [isRefreshingAvatars, setIsRefreshingAvatars] = useState(false);
  const [looksModalGroup, setLooksModalGroup] = useState<AvatarModel | null>(null);
  // Group_id of the currently selected avatar/look — so a card stays "selected" even when
  // the chosen look (outfit/pose) has a different id than the group's default card id.
  const [selectedAvatarGroupId, setSelectedAvatarGroupId] = useState<string | null>(null);
  const [looksInModal, setLooksInModal] = useState<AvatarLook[]>([]);
  const [isLoadingLooks, setIsLoadingLooks] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
  const voicePreviewRef = useRef<HTMLAudioElement | null>(null);
  const [previewingAvatarId, setPreviewingAvatarId] = useState<string | null>(null);
  const avatarPreviewRef = useRef<HTMLAudioElement | null>(null);
  const [originalTextBeforeEnhancement, setOriginalTextBeforeEnhancement] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{
    moduleConfig?: string;
    contentConfig?: string;
    assessmentConfig?: string;
  }>({});

  // Pagination state
  const [historyPage, setHistoryPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(6);

  // Initialize react-hook-form
  const { control, handleSubmit: rhfHandleSubmit, watch, setValue, formState: { errors } } = useForm<CourseFormData>({
    mode: 'all',
    defaultValues: {
      courseIdea: '',
      targetAudience: 'Beginners',
      difficultyLevel: DifficultyLevel.INTERMEDIATE,
      tone: Tone.FORMAL,
      knowledgeStoreId: '',
      knowledgeStoreOnlyMode: false,
      exampleInjectionEnabled: true,
      prerequisites: [],
      totalDuration: 2,
      industry: '',
      pedagogyFramework: 'DEFAULT',
      languageCode: initial_language_code,
      translationLanguages: [],
      translationPicker: '',
      deviceType: 'desktop',
      ttsConfig: {
        voiceType: 'Standard',
        voiceName: '',
        speakingRate: 1.0,
        pitch: 0,
        volumeGainDb: 0,
      },
      captionConfig: {
        enableCaptions: true,
        format: 'webvtt',
        maxDuration: 3.0,
        minDuration: 1.0,
      },
      htmlSlidesConfig: {
        contentFormat: 'html',
        theme: 'educational',
        videoRenderingMode: 'static',
        contentTypes: ['html'],
      },
      moduleGenerationConfig: {
        minModules: 3,
        maxModules: 4,
      },
      contentGenerationConfig: {
        minContentItemsPerModule: 3,
        maxContentItemsPerModule: 4,
      },
      sceneGenerationConfig: {
        minScenesPerContent: 4,
        maxScenesPerContent: 6,
      },
      contentDeliveryType: 'html',
      avatarConfig: {
        provider: 'heygen',
        videoMode: 'full_animated_video',
        compositionMode: 'fullscreen',
        sourceUrl: '',
        avatarId: '',
        voiceId: '',
        useVideoAgent: false,
        resolution: '720p',
        engine: 'avatar_3',
        targetDurationSeconds: 180, // 3 min default for Video Agent (full_animated_video)
        position: 'bottom_right',
        pipSizePercent: 25,
        pipShape: 'circle',
        pipBorder: true,
        backgroundColor: '#FFFFFF',
      },
      enableEnhancedFeatures: false,
      enhancedFeatures: {
        autoGrading: false,
        selfCheckQuizzes: false,
        adaptiveLearning: false,
        analytics: false,
        collaborative: false,
        microlearning: false,
      },
      disableAssessments: false,
      assessmentConfig: {
        enabled: false,
        questionTypes: ['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE'],
        contentItemAssessments: {
          generatePre: false,
          generatePost: true,
          minQuestions: 3,
          maxQuestions: 8,
          passingScore: 70,
          timeLimitMinutes: 10
        },
        moduleAssessments: {
          generatePre: false,
          generatePost: true,
          minQuestions: 8,
          maxQuestions: 10,
          passingScore: 70,
          timeLimitMinutes: 30
        },
        courseAssessments: {
          generatePre: false,
          generatePost: true,
          minQuestions: 8,
          maxQuestions: 10,
          passingScore: 70,
          timeLimitMinutes: 60
        },
        immediateFeedback: true,
        allowRetry: true,
        maxAttempts: 3,
        shuffleQuestions: false,
        shuffleOptions: true,
        showCorrectAnswers: true
      }
    }
  });

  // Watch form values - provides access to all form data
  const formData = watch();

  // Per-tenant avatar_video enablement, fetched from the backend (replaces the
  // build-time VITE_AVATAR_VIDEO_DOMAINS gate). Defaults to disabled until loaded.
  const [avatarVideoEnabled, setAvatarVideoEnabled] = useState(false);
  useEffect(() => {
    courseService.getCapabilities()
      .then(c => setAvatarVideoEnabled(!!c.avatar_video_enabled))
      .catch(() => setAvatarVideoEnabled(false));
  }, []);

  // Talking-head / composition / PiP avatar controls are hidden for now — only the
  // HeyGen full-animated-video mode is supported in the UI. Flip to true to re-enable
  // (planned next month). Keeps the code intact so re-enabling is a one-line change.
  const ENABLE_TALKING_HEAD_CONTROLS = false;

  // Avatar source filter (All / Public / Private) is hidden for now — we always show
  // "All". Re-enable when private (uploaded) avatars are productized. Source stays 'all'.
  const ENABLE_AVATAR_SOURCE_FILTER = false;

  // ── HeyGen full-animated-video structure caps ───────────────────────────────
  // HeyGen renders go through a shared account with a low concurrent-generation
  // limit; each content item is a separate (40+ min) render. Cap the course size so
  // a single course can't queue an unbounded number of renders. (UI-only guard.)
  const HEYGEN_MAX_MODULES = 3;
  const HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE = 2;
  const isHeygenFullAnimated =
    formData.contentDeliveryType === 'avatar_video' &&
    formData.avatarConfig?.videoMode === 'full_animated_video' &&
    formData.avatarConfig?.provider === 'heygen';

  // On switching INTO HeyGen full-animated mode, default to 1 module / 1 content item.
  // While in that mode, just clamp to the caps (don't keep forcing 1 — respect the slider).
  const wasHeygenFullAnimatedRef = useRef(false);
  useEffect(() => {
    if (isHeygenFullAnimated && !wasHeygenFullAnimatedRef.current) {
      // Just entered avatar mode — start at the minimal 1/1 to keep render count tiny.
      setValue('moduleGenerationConfig.minModules', 1);
      setValue('moduleGenerationConfig.maxModules', 1);
      setValue('contentGenerationConfig.minContentItemsPerModule', 1);
      setValue('contentGenerationConfig.maxContentItemsPerModule', 1);
    } else if (isHeygenFullAnimated) {
      // Safety clamp to the HeyGen caps.
      if (formData.moduleGenerationConfig.maxModules > HEYGEN_MAX_MODULES) {
        setValue('moduleGenerationConfig.maxModules', HEYGEN_MAX_MODULES);
      }
      if (formData.moduleGenerationConfig.minModules > HEYGEN_MAX_MODULES) {
        setValue('moduleGenerationConfig.minModules', HEYGEN_MAX_MODULES);
      }
      if (formData.contentGenerationConfig.maxContentItemsPerModule > HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE) {
        setValue('contentGenerationConfig.maxContentItemsPerModule', HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE);
      }
      if (formData.contentGenerationConfig.minContentItemsPerModule > HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE) {
        setValue('contentGenerationConfig.minContentItemsPerModule', HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE);
      }
    }
    wasHeygenFullAnimatedRef.current = isHeygenFullAnimated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHeygenFullAnimated,
    formData.moduleGenerationConfig.minModules,
    formData.moduleGenerationConfig.maxModules,
    formData.contentGenerationConfig.minContentItemsPerModule,
    formData.contentGenerationConfig.maxContentItemsPerModule,
  ]);

  // Fetch avatar models when avatar_video is selected and provider changes
  // Uses a request token to avoid race conditions when switching providers quickly
  // Uses HeyGen's cursor pagination — fetches first page, "Show more" loads next page
  const avatarFetchTokenRef = useRef(0);
  const fetchAvatarModels = async (provider?: 'd_id' | 'heygen') => {
    const token = ++avatarFetchTokenRef.current;
    setIsLoadingAvatars(true);
    setAvatarNextToken(null);
    setAvatarHasMore(false);
    try {
      const result = await avatarModelsService.listAvatarModels(provider, {
        limit: 8,
        search: avatarSearch.trim() || undefined,
        source: avatarSourceFilter === 'all' ? undefined : avatarSourceFilter,
      });
      if (token !== avatarFetchTokenRef.current) return; // stale response
      setAvatarModels(result.avatars);
      setAvatarNextToken(result.next_token);
      setAvatarHasMore(result.has_more);
      // Fallback: if we got avatars back from a specific provider, it's clearly configured
      // even if the API didn't report configured_providers correctly
      const inferred = { ...result.configured_providers };
      if (provider && result.avatars.length > 0) {
        inferred[provider] = true;
      }
      setConfiguredProviders(inferred);
    } catch {
      if (token === avatarFetchTokenRef.current) setAvatarModels([]);
    } finally {
      if (token === avatarFetchTokenRef.current) setIsLoadingAvatars(false);
    }
  };

  const openLooksModal = async (avatar: AvatarModel) => {
    const group_id = avatar.metadata?.group_id || avatar.id;
    setLooksModalGroup(avatar);
    setLooksInModal([]);
    setIsLoadingLooks(true);
    try {
      const looks = await avatarModelsService.listLooksForGroup(group_id);
      setLooksInModal(looks);
    } finally {
      setIsLoadingLooks(false);
    }
  };

  const selectLook = (look: AvatarLook) => {
    setValue('avatarConfig.sourceUrl' as any, look.preview_image_url || look.id);
    setValue('avatarConfig.avatarId' as any, look.id);
    // Remember which group this look belongs to so its card shows as selected.
    setSelectedAvatarGroupId(looksModalGroup?.metadata?.group_id || looksModalGroup?.id || null);
    setLooksModalGroup(null);
  };

  const loadMoreAvatars = async () => {
    if (!avatarNextToken || isLoadingMoreAvatars) return;
    setIsLoadingMoreAvatars(true);
    try {
      const result = await avatarModelsService.listAvatarModels(
        formData.avatarConfig?.provider,
        {
          limit: 8,
          token: avatarNextToken,
          search: avatarSearch.trim() || undefined,
          source: avatarSourceFilter === 'all' ? undefined : avatarSourceFilter,
        }
      );
      setAvatarModels(prev => [...prev, ...result.avatars]);
      setAvatarNextToken(result.next_token);
      setAvatarHasMore(result.has_more);
    } catch {
      // silent fail — keep existing list
    } finally {
      setIsLoadingMoreAvatars(false);
    }
  };

  const handleRefreshAvatars = async () => {
    if (isRefreshingAvatars) return;
    setIsRefreshingAvatars(true);
    try {
      const result = await avatarModelsService.refreshAvatarCache();
      if (!result) {
        toast.error('Failed to refresh avatar catalog');
      } else if (result.skipped_reason) {
        toast.error(result.description || `Sync skipped: ${result.skipped_reason}`);
      } else {
        toast.success(result.description || `Avatar catalog refreshed — ${result.upserted} avatars synced`);
      }
      // Refetch the picker after sync completes
      await fetchAvatarModels(formData.avatarConfig?.provider);
    } finally {
      setIsRefreshingAvatars(false);
    }
  };

  useEffect(() => {
    if (formData.contentDeliveryType === 'avatar_video') {
      fetchAvatarModels(formData.avatarConfig?.provider);
    }
  }, [formData.contentDeliveryType, formData.avatarConfig?.provider]);

  // Debounce search + source filter changes — 350ms after user stops typing
  useEffect(() => {
    if (formData.contentDeliveryType !== 'avatar_video') return;
    const handle = setTimeout(() => {
      fetchAvatarModels(formData.avatarConfig?.provider);
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarSearch, avatarSourceFilter]);

  // Fetch HeyGen voices when full_animated_video is selected, filtered by course language
  const fetchAvatarVoices = async (language_code?: string) => {
    setIsLoadingVoices(true);
    try {
      const heygen_language = language_code ? LOCALE_TO_HEYGEN_LANGUAGE[language_code] : undefined;
      const voices = await avatarModelsService.listVoices(heygen_language);
      setAvatarVoices(voices);
      // Reset voice selection if current voice no longer in filtered list
      if (formData.avatarConfig?.voiceId) {
        const still_valid = voices.some(v => v.voice_id === formData.avatarConfig.voiceId);
        if (!still_valid) setValue('avatarConfig.voiceId' as any, '');
      }
    } catch {
      setAvatarVoices([]);
    } finally {
      setIsLoadingVoices(false);
    }
  };

  useEffect(() => {
    if (
      formData.contentDeliveryType === 'avatar_video' &&
      formData.avatarConfig?.provider === 'heygen' &&
      formData.avatarConfig?.videoMode === 'full_animated_video'
    ) {
      fetchAvatarVoices(formData.languageCode);
    }
  }, [formData.contentDeliveryType, formData.avatarConfig?.provider, formData.avatarConfig?.videoMode, formData.languageCode]);

  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['orchestrationHistory', historyPage, historyItemsPerPage],
    queryFn: () => courseGenerationService.getCourseOrchestrationList({
      limit: historyItemsPerPage,
      skip: (historyPage - 1) * historyItemsPerPage,
    }),
  });

  const { data: documentStoresData } = useQuery({
    queryKey: ['document-stores-dropdown'],
    queryFn: () => documentStoreService.getDropdownList(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  useEffect(() => {
    if (trendingTopic) {
      setValue('courseIdea', `${trendingTopic.title} - ${trendingTopic.desc}`, { shouldValidate: true });
      // Only set targetAudience if it exists in trendingTopic, otherwise use default value
      if (trendingTopic.audience) {
        setValue('targetAudience', trendingTopic.audience, { shouldValidate: true });
      }
    }
  }, [trendingTopic, setValue]);

  // Pre-fill from ticket description (from self-ticket request)
  useEffect(() => {
    if (ticketDescription) {
      setValue('courseIdea', ticketDescription, { shouldValidate: true });
    }
  }, [ticketDescription, setValue]);

  // Pre-fill language from ticket
  // A choice in the Select becomes a chip and the Select resets, so it always
  // reads "Add a language" and can be used again immediately.
  useEffect(() => {
    const picked = formData.translationPicker;
    if (!picked) return;

    const option = languageOptions.find((o) => o.value === picked);
    const already = (formData.translationLanguages ?? []).some((l) => l.language_code === picked);

    // No count limit. The list of languages a tenant wants is theirs to decide;
    // what stops runaway selection is that each one is visible in the list below
    // with its cost, and they run one at a time so any of them can be abandoned.
    if (option && !already) {
      setValue('translationLanguages' as any, [
        ...(formData.translationLanguages ?? []),
        { language_code: option.value, language_name: option.label },
      ]);
    }
    setValue('translationPicker' as any, '');
  }, [formData.translationPicker, formData.translationLanguages, setValue]);

  // Switching the master language must remove it from the translation list —
  // a course cannot be translated into the language it is written in, and the
  // API rejects it. Cheaper to prevent than to explain.
  useEffect(() => {
    const picked = formData.translationLanguages ?? [];
    if (picked.some((l) => l.language_code === formData.languageCode)) {
      setValue(
        'translationLanguages' as any,
        picked.filter((l) => l.language_code !== formData.languageCode)
      );
    }
  }, [formData.languageCode, formData.translationLanguages, setValue]);

  useEffect(() => {
    if (ticketLanguageCode) {
      setValue('languageCode', ticketLanguageCode);
    }
  }, [ticketLanguageCode, setValue]);

  // Pre-fill course generation config from ticket
  useEffect(() => {
    if (courseGenerationConfig) {
      if (courseGenerationConfig.content_delivery_type) {
        setValue('contentDeliveryType', courseGenerationConfig.content_delivery_type);
      }
      if (courseGenerationConfig.design_theme) {
        setValue('htmlSlidesConfig.theme' as any, courseGenerationConfig.design_theme);
      }
      if (courseGenerationConfig.module_generation_config) {
        setValue('moduleGenerationConfig.minModules', courseGenerationConfig.module_generation_config.min_modules);
        setValue('moduleGenerationConfig.maxModules', courseGenerationConfig.module_generation_config.max_modules);
      }
      if (courseGenerationConfig.content_generation_config) {
        setValue('contentGenerationConfig.minContentItemsPerModule', courseGenerationConfig.content_generation_config.min_content_items_per_module);
        setValue('contentGenerationConfig.maxContentItemsPerModule', courseGenerationConfig.content_generation_config.max_content_items_per_module);
      }
    }
  }, [courseGenerationConfig, setValue]);

  // Pre-fill form from URL query parameters (for course generation suggestions)
  useEffect(() => {
    const idea = searchParams.get('idea');
    const audience = searchParams.get('audience');
    const difficulty = searchParams.get('difficulty');
    const language = searchParams.get('language');

    if (idea) {
      setValue('courseIdea', idea, { shouldValidate: true });
    }
    if (audience) {
      setValue('targetAudience', audience, { shouldValidate: true });
    }
    if (difficulty) {
      // Map difficulty string to DifficultyLevel enum
      const difficultyMap: Record<string, DifficultyLevel> = {
        'beginner': DifficultyLevel.BEGINNER,
        'intermediate': DifficultyLevel.INTERMEDIATE,
        'advanced': DifficultyLevel.ADVANCED,
      };
      const mappedDifficulty = difficultyMap[difficulty.toLowerCase()];
      if (mappedDifficulty) {
        setValue('difficultyLevel', mappedDifficulty, { shouldValidate: true });
      }
    }
    // Course language piped from the LP build-suggestion flow. Accept exact
    // locale matches first ('hi-IN'), then fall back to prefix match
    // ('hi' → 'hi-IN') so Gemini's loosely-formatted suggestion.language
    // values still bind the picker correctly.
    if (language) {
      const lc = language.toLowerCase();
      const exact = languageOptions.find((opt) => opt.value.toLowerCase() === lc);
      const prefix = exact
        ? exact
        : languageOptions.find((opt) => opt.value.toLowerCase().split('-')[0] === lc.split('-')[0]);
      if (prefix) {
        setValue('languageCode', prefix.value, { shouldValidate: true });
      }
    }
  }, [searchParams, setValue]);

  // Auto-reset voice type when language changes if current type is not available
  useEffect(() => {
    const current_voice_type = formData.ttsConfig?.voiceType;
    const language_code = formData.languageCode;
    if (language_code && current_voice_type && !isVoiceTypeAvailable(language_code, current_voice_type)) {
      setValue('ttsConfig.voiceType' as any, getDefaultVoiceType(language_code));
    }
  }, [formData.languageCode, formData.ttsConfig?.voiceType, setValue]);

  // Auto-reset voice name when voice type or language changes
  useEffect(() => {
    const language_code = formData.languageCode;
    const voice_type = formData.ttsConfig?.voiceType;
    const current_voice_name = formData.ttsConfig?.voiceName;
    if (language_code && voice_type) {
      const available_voices = getVoicesForLanguageAndType(language_code, voice_type);
      const is_valid = available_voices.some(v => v.voice_name === current_voice_name);
      if (!is_valid) {
        setValue('ttsConfig.voiceName' as any, available_voices[0]?.voice_name || '');
      }
      // Reset pitch to 0 when switching to a voice type that doesn't support pitch
      if (!isVoiceTypePitchSupported(voice_type) && formData.ttsConfig?.pitch !== 0) {
        setValue('ttsConfig.pitch' as any, 0);
      }
    }
  }, [formData.languageCode, formData.ttsConfig?.voiceType, formData.ttsConfig?.voiceName, formData.ttsConfig?.pitch, setValue]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [formData.courseIdea]);

  const generateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Transform formData to match backend API expectations
      const payload: any = {
        user_course_idea: data.courseIdea,
        targeted_audience: data.targetAudience,
        difficulty_level: data.difficultyLevel,
        tone: data.tone,
        pedagogy_framework: data.pedagogyFramework,

        // Course Structure
        prerequisites: data.prerequisites,
        total_duration: data.totalDuration,
        industry: data.industry || '',

        // Knowledge Store (always send proper defaults)
        knowledge_store_id: data.knowledgeStoreId || null,
        knowledge_store_only_mode: data.knowledgeStoreOnlyMode,
        example_injection_enabled: data.knowledgeStoreId ? data.exampleInjectionEnabled : false,
        enable_knowledge_store: !!data.knowledgeStoreId,
        enable_example_injection_store: data.knowledgeStoreId ? data.exampleInjectionEnabled : false,

        // HTML Slides Configuration (always send basic config)
        html_slides_config: {
          content_format: data.htmlSlidesConfig.contentFormat,
          theme: data.htmlSlidesConfig.theme,
          video_rendering_mode: data.htmlSlidesConfig.videoRenderingMode,
          content_types: data.htmlSlidesConfig.contentTypes,
        },

        // Module Generation Configuration (always send)
        module_generation_config: {
          min_modules: data.moduleGenerationConfig.minModules,
          max_modules: data.moduleGenerationConfig.maxModules,
        },

        // Content Generation Configuration (always send)
        content_generation_config: {
          min_content_items_per_module: data.contentGenerationConfig.minContentItemsPerModule,
          max_content_items_per_module: data.contentGenerationConfig.maxContentItemsPerModule,
        },

        // Scene Generation Configuration (skip for full_animated — no scenes needed)
        ...(!(data.contentDeliveryType === 'avatar_video' && data.avatarConfig.videoMode === 'full_animated_video' && data.avatarConfig.provider === 'heygen') && {
          scene_generation_config: {
            min_scenes_per_content: data.sceneGenerationConfig.minScenesPerContent,
            max_scenes_per_content: data.sceneGenerationConfig.maxScenesPerContent,
          },
        }),

        // Content Delivery Type (single-select)
        content_delivery_type: data.contentDeliveryType,

        // Avatar configuration (only when avatar_video selected)
        ...(data.contentDeliveryType === 'avatar_video' && {
          avatar_config: {
            provider: data.avatarConfig.provider,
            video_mode: data.avatarConfig.videoMode,
            composition_mode: data.avatarConfig.compositionMode,
            source_url: data.avatarConfig.sourceUrl,
            avatar_id: data.avatarConfig.avatarId || undefined,
            voice_id: data.avatarConfig.voiceId || undefined,
            use_video_agent: data.avatarConfig.videoMode === 'full_animated_video' ? true : undefined,
            resolution: data.avatarConfig.resolution || '720p',
            engine: data.avatarConfig.engine || 'avatar_4',
            target_duration_seconds: data.avatarConfig.videoMode === 'full_animated_video'
              ? (data.avatarConfig.targetDurationSeconds || 180)
              : undefined,
            position: data.avatarConfig.position,
            pip_size_percent: data.avatarConfig.pipSizePercent,
            pip_shape: data.avatarConfig.pipShape,
            pip_border: data.avatarConfig.pipBorder,
            background_color: data.avatarConfig.backgroundColor,
          },
        }),

        // Enhanced Features (always send)
        enable_enhanced_features: data.enableEnhancedFeatures,
        enhanced_features: {
          auto_grading: data.enhancedFeatures.autoGrading,
          self_check_quizzes: data.enhancedFeatures.selfCheckQuizzes,
          adaptive_learning: data.enhancedFeatures.adaptiveLearning,
          analytics: data.enhancedFeatures.analytics,
          collaborative: data.enhancedFeatures.collaborative,
          microlearning: data.enhancedFeatures.microlearning,
        },

        // Comprehensive Assessment Configuration
        assessment_config: data.assessmentConfig.enabled ? {
          enabled: data.assessmentConfig.enabled,
          question_types: data.assessmentConfig.questionTypes,
          content_item_assessments: {
            generate_pre: data.assessmentConfig.contentItemAssessments.generatePre,
            generate_post: data.assessmentConfig.contentItemAssessments.generatePost,
            min_questions: Number(data.assessmentConfig.contentItemAssessments.minQuestions),
            max_questions: Number(data.assessmentConfig.contentItemAssessments.maxQuestions),
            passing_score: Number(data.assessmentConfig.contentItemAssessments.passingScore),
            time_limit_minutes: Number(data.assessmentConfig.contentItemAssessments.timeLimitMinutes)
          },
          module_assessments: {
            generate_pre: data.assessmentConfig.moduleAssessments.generatePre,
            generate_post: data.assessmentConfig.moduleAssessments.generatePost,
            min_questions: Number(data.assessmentConfig.moduleAssessments.minQuestions),
            max_questions: Number(data.assessmentConfig.moduleAssessments.maxQuestions),
            passing_score: Number(data.assessmentConfig.moduleAssessments.passingScore),
            time_limit_minutes: Number(data.assessmentConfig.moduleAssessments.timeLimitMinutes)
          },
          course_assessments: {
            generate_pre: data.assessmentConfig.courseAssessments.generatePre,
            generate_post: data.assessmentConfig.courseAssessments.generatePost,
            min_questions: Number(data.assessmentConfig.courseAssessments.minQuestions),
            max_questions: Number(data.assessmentConfig.courseAssessments.maxQuestions),
            passing_score: Number(data.assessmentConfig.courseAssessments.passingScore),
            time_limit_minutes: Number(data.assessmentConfig.courseAssessments.timeLimitMinutes)
          },
          immediate_feedback: data.assessmentConfig.immediateFeedback,
          allow_retry: data.assessmentConfig.allowRetry,
          max_attempts: data.assessmentConfig.maxAttempts,
          shuffle_questions: data.assessmentConfig.shuffleQuestions,
          shuffle_options: data.assessmentConfig.shuffleOptions,
          show_correct_answers: data.assessmentConfig.showCorrectAnswers
        } : undefined,
      };

      // Ensure all critical defaults are set properly
      const defaultsCheck = {
        hasTargetAudience: !!payload.targeted_audience,
        hasDifficultyLevel: !!payload.difficulty_level,
        hasTone: !!payload.tone,
        hasPedagogyFramework: !!payload.pedagogy_framework,
        hasModuleConfig: !!payload.module_generation_config,
        hasContentConfig: !!payload.content_generation_config,
        hasSceneConfig: !!payload.scene_generation_config,
        hasContentDeliveryType: !!payload.content_delivery_type,
        hasLanguageConfig: !!payload.language_config,
        hasHtmlConfig: !!payload.html_slides_config
      };


      // Warn if any critical defaults are missing
      const missingDefaults = Object.entries(defaultsCheck)
        .filter(([_, value]) => !value)
        .map(([key]) => key);

      if (missingDefaults.length > 0) {
        console.warn('Missing default values:', missingDefaults);
      }

      // Debug: Log the payload being sent

      // Build language_config conditionally based on content delivery type
      // Skip Google TTS for full_animated_video — Video Agent handles everything including voice
      const uses_heygen_tts = data.contentDeliveryType === 'avatar_video'
        && data.avatarConfig.videoMode === 'full_animated_video'
        && data.avatarConfig.provider === 'heygen';
      const needs_tts = data.contentDeliveryType !== 'html' && !uses_heygen_tts;
      const needs_captions = data.contentDeliveryType === 'video_presentation' || data.contentDeliveryType === 'avatar_video';

      // Validate TTS only when needed
      if (needs_tts) {
        const ttsValidation = languageConfigService.validateTTSConfig(data.ttsConfig);
        if (!ttsValidation.valid) {
          throw new Error(`TTS Configuration Error: ${ttsValidation.errors.join(', ')}`);
        }
      }

      // Validate captions only when needed (skip for full_animated — always enabled with defaults)
      if (needs_captions && !uses_heygen_tts) {
        const captionValidation = languageConfigService.validateCaptionConfig(data.captionConfig);
        if (!captionValidation.valid) {
          throw new Error(`Caption Configuration Error: ${captionValidation.errors.join(', ')}`);
        }
      }

      payload.language_config = {
        language_code: data.languageCode,
        device_type: data.deviceType,
        // Empty array omitted so an untranslated course sends nothing extra.
        ...((data.translationLanguages?.length ?? 0) > 0 && {
          translation_languages: data.translationLanguages,
        }),
        ...(needs_tts && {
          tts_config: {
            voice_name: data.ttsConfig.voiceName,
            voice_type: data.ttsConfig.voiceType,
            speaking_rate: data.ttsConfig.speakingRate,
            pitch: data.ttsConfig.pitch,
            volume_gain_db: data.ttsConfig.volumeGainDb,
          },
        }),
        ...(needs_captions && {
          caption_config: uses_heygen_tts
            ? { enable_captions: true, format: 'webvtt', max_duration: 3, min_duration: 1 }
            : {
                enable_captions: data.captionConfig.enableCaptions,
                format: data.captionConfig.format,
                max_duration: data.captionConfig.maxDuration,
                min_duration: data.captionConfig.minDuration,
              },
        }),
      };
      
      return courseGenerationService.initiateCourseGeneration(payload);
    },
    onSuccess: (response: CourseOrchestration) => {
      const newOrchestrationId = response.id || response._id;
      toast.success('Course generation started successfully!');
      navigate(`/admin/course/build-course?orchestrationId=${newOrchestrationId}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to start course generation'
      );
      setIsGenerating(false);
    },
  });

  // Validation function for advanced settings
  const validateAdvancedSettings = (data: CourseFormData): boolean => {
    const errors: typeof validationErrors = {};
    let isValid = true;

    // Validate module configuration
    if (data.moduleGenerationConfig.minModules > data.moduleGenerationConfig.maxModules) {
      errors.moduleConfig = 'Min modules cannot be greater than max modules';
      isValid = false;
    }

    // Validate content configuration
    if (data.contentGenerationConfig.minContentItemsPerModule > data.contentGenerationConfig.maxContentItemsPerModule) {
      errors.contentConfig = 'Min content items cannot be greater than max content items';
      isValid = false;
    }

    // HeyGen full-animated courses are capped (shared account / low concurrent-render
    // limit; each content item is a separate 40+ min render).
    const heygenFullAnimated =
      data.contentDeliveryType === 'avatar_video' &&
      data.avatarConfig?.videoMode === 'full_animated_video' &&
      data.avatarConfig?.provider === 'heygen';
    if (heygenFullAnimated) {
      if (
        data.moduleGenerationConfig.maxModules > HEYGEN_MAX_MODULES ||
        data.moduleGenerationConfig.minModules > HEYGEN_MAX_MODULES
      ) {
        errors.moduleConfig = `HeyGen avatar videos are limited to ${HEYGEN_MAX_MODULES} modules`;
        isValid = false;
      }
      if (
        data.contentGenerationConfig.maxContentItemsPerModule > HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE ||
        data.contentGenerationConfig.minContentItemsPerModule > HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE
      ) {
        errors.contentConfig = `HeyGen avatar videos are limited to ${HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE} content items per module`;
        isValid = false;
      }
    }

    // Validate assessment configuration
    if (data.assessmentConfig.enabled) {
      // Check if at least one question type is selected
      if (data.assessmentConfig.questionTypes.length === 0) {
        errors.assessmentConfig = 'Please select at least one question type when assessment is enabled';
        isValid = false;
      }

      // Validate content item assessments
      const contentItemAssessments = data.assessmentConfig.contentItemAssessments;
      if (contentItemAssessments.generatePre || contentItemAssessments.generatePost) {
        if (Number(contentItemAssessments.minQuestions) > Number(contentItemAssessments.maxQuestions)) {
          errors.assessmentConfig = 'Content item: Min questions cannot be greater than max questions';
          isValid = false;
        }
        if (contentItemAssessments.passingScore < 1 || contentItemAssessments.passingScore > 100) {
          errors.assessmentConfig = 'Content item: Passing percentage must be between 1 and 100';
          isValid = false;
        }
      }

      // Validate module assessments
      const moduleAssessments = data.assessmentConfig.moduleAssessments;
      if (moduleAssessments.generatePre || moduleAssessments.generatePost) {
        if (Number(moduleAssessments.minQuestions) > Number(moduleAssessments.maxQuestions)) {
          errors.assessmentConfig = 'Module: Min questions cannot be greater than max questions';
          isValid = false;
        }
        if (moduleAssessments.passingScore < 1 || moduleAssessments.passingScore > 100) {
          errors.assessmentConfig = 'Module: Passing percentage must be between 1 and 100';
          isValid = false;
        }
      }

      // Validate course assessments
      const courseAssessments = data.assessmentConfig.courseAssessments;
      if (courseAssessments.generatePre || courseAssessments.generatePost) {
        if (Number(courseAssessments.minQuestions) > Number(courseAssessments.maxQuestions)) {
          errors.assessmentConfig = 'Course: Min questions cannot be greater than max questions';
          isValid = false;
        }
        if (courseAssessments.passingScore < 1 || courseAssessments.passingScore > 100) {
          errors.assessmentConfig = 'Course: Passing percentage must be between 1 and 100';
          isValid = false;
        }
      }
    }

    setValidationErrors(errors);
    return isValid;
  };

  const onSubmit = rhfHandleSubmit((data: CourseFormData) => {
    // Validate advanced settings
    if (!validateAdvancedSettings(data)) {
      toast.error('Please fix validation errors in Advanced Settings');
      setShowSettings(true); // Open advanced settings to show errors
      return;
    }

    // Avatar selection is mandatory for avatar videos. If no avatar is chosen the
    // provider would silently pick one based on the content — block that and force an
    // explicit choice from the picker.
    if (data.contentDeliveryType === 'avatar_video' && !data.avatarConfig.avatarId) {
      toast.error('Please select an avatar before generating the avatar video.');
      return;
    }

    // Generating from a knowledge store at a size nothing analysed is the one case worth a
    // second look: the documents were read and a range was suggested, and ignoring it is
    // usually an oversight rather than a decision. Hold the validated payload so confirming
    // generates exactly what was submitted here.
    if (data.knowledgeStoreId && !isSuggestionApplied) {
      pendingGenerationRef.current = data;
      setShowAiWarning(true);
      return;
    }

    setIsGenerating(true);
    generateMutation.mutate(data);
  });

  const orchestrationHistory: CourseOrchestration[] = historyData?.data?.courseOrchestrations?.sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  ) || [];

  const totalHistory = historyData?.data?.total_count || 0;
  const totalPages = historyData?.data?.total_count ? Math.ceil(historyData.data.total_count / historyItemsPerPage) : 1;

  const handleHistoryItemsPerPageChange = (newItemsPerPage: number) => {
    setHistoryItemsPerPage(newItemsPerPage);
    setHistoryPage(1); // Reset to first page when changing items per page
  };

  // Helper function to check if course generation is complete
  const isCourseGenerationComplete = (orchestration: CourseOrchestration) => {
    // Use orchestration_status for overall completion check
    return orchestration.orchestration_status?.toLowerCase() === 'completed';
  };

  const getStatusColor = (orchestration: CourseOrchestration) => {
    if (isCourseGenerationComplete(orchestration)) {
      return 'text-green-600 bg-green-50 border-green-200';
    }

    // Check if any stage is processing
    const allStages = [
      orchestration.metadata_generation_status?.status,
      orchestration.image_generation_status?.status,
      orchestration.course_skeleton_generation_status?.status,
      orchestration.module_content_expansion_status?.status
    ];

    const hasProcessing = allStages.some(status =>
      status?.toLowerCase() === 'in_progress' || status?.toLowerCase() === 'processing'
    );

    const hasFailed = allStages.some(status => status?.toLowerCase() === 'failed');

    if (hasFailed) {
      return 'text-red-600 bg-red-50 border-red-200';
    } else if (hasProcessing) {
      return 'text-blue-600 bg-blue-50 border-blue-200';
    } else {
      return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (orchestration: CourseOrchestration) => {
    if (isCourseGenerationComplete(orchestration)) {
      return <CheckCircle className="w-4 h-4" />;
    }

    // Check if any stage is processing
    const allStages = [
      orchestration.metadata_generation_status?.status,
      orchestration.image_generation_status?.status,
      orchestration.course_skeleton_generation_status?.status,
      orchestration.module_content_expansion_status?.status
    ];

    const hasProcessing = allStages.some(status =>
      status?.toLowerCase() === 'in_progress' || status?.toLowerCase() === 'processing'
    );

    const hasFailed = allStages.some(status => status?.toLowerCase() === 'failed');

    if (hasFailed) {
      return <XCircle className="w-4 h-4" />;
    } else if (hasProcessing) {
      return <Loader2 className="w-4 h-4 animate-spin" />;
    } else {
      return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusText = (orchestration: CourseOrchestration) => {
    if (isCourseGenerationComplete(orchestration)) {
      return 'Completed';
    }

    // Check if any stage is processing
    const allStages = [
      orchestration.metadata_generation_status?.status,
      orchestration.image_generation_status?.status,
      orchestration.course_skeleton_generation_status?.status,
      orchestration.module_content_expansion_status?.status
    ];

    const hasProcessing = allStages.some(status =>
      status?.toLowerCase() === 'in_progress' || status?.toLowerCase() === 'processing'
    );

    const hasFailed = allStages.some(status => status?.toLowerCase() === 'failed');

    if (hasFailed) {
      return 'Failed';
    } else if (hasProcessing) {
      return 'In Progress';
    } else {
      return 'Pending';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  // The ceilings the builder itself enforces, handed to the analysis so its suggestion is
  // always something these controls will accept. HeyGen full-animated courses are capped far
  // tighter because every content item is a separate render.
  const analysisLimits = {
    maxModules: isHeygenFullAnimated ? HEYGEN_MAX_MODULES : 10,
    maxContentItemsPerModule: isHeygenFullAnimated ? HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE : 6,
    minScenesPerContent: 4,
    maxScenesPerContent: 10,
    // Avatar courses have no scenes — they are sized by the length of each rendered video,
    // which the Target Duration slider caps at six minutes.
    minDurationSeconds: 60,
    maxDurationSeconds: 360,
  };

  // Take the analysed range straight onto the controls. Min and max are kept as a real range
  // so the generator has the same latitude the analysis judged the material to support.
  const applyAnalyzedStructure = (suggested: SuggestedStructureRange) => {
    setValue('moduleGenerationConfig.minModules', suggested.min_modules);
    setValue('moduleGenerationConfig.maxModules', suggested.max_modules);
    setValue('contentGenerationConfig.minContentItemsPerModule', suggested.min_content_items_per_module);
    setValue('contentGenerationConfig.maxContentItemsPerModule', suggested.max_content_items_per_module);

    // Scenes come back only for video presentations; the other types have none to set.
    if (suggested.min_scenes_per_content && suggested.max_scenes_per_content) {
      setValue('sceneGenerationConfig.minScenesPerContent' as any, suggested.min_scenes_per_content);
      setValue('sceneGenerationConfig.maxScenesPerContent' as any, suggested.max_scenes_per_content);
    }

    // Avatar courses are sized by video length instead. Take the top of the suggested range:
    // it is the length the material actually fills, already clamped to the six-minute cap.
    if (suggested.max_duration_seconds) {
      setValue('avatarConfig.targetDurationSeconds' as any, suggested.max_duration_seconds);
    }

    setValidationErrors(prev => ({ ...prev, moduleConfig: undefined, contentConfig: undefined }));
    toast.success('Course size updated to the suggested range');
  };

  /**
   * Whether the controls currently hold the analysed suggestion.
   *
   * Read from the actual values rather than remembered from a click, so setting the sliders by
   * hand to the suggested numbers counts as having taken the advice — and moving them away
   * again after clicking "Use this range" stops counting. A flag could only ever describe how
   * the values got there, which is not what either the button or the confirmation is about.
   */
  const isSuggestionApplied = (() => {
    const s = analyzedSuggestion;
    if (!s) return false;

    if (formData.moduleGenerationConfig.minModules !== s.min_modules) return false;
    if (formData.moduleGenerationConfig.maxModules !== s.max_modules) return false;
    if (formData.contentGenerationConfig.minContentItemsPerModule !== s.min_content_items_per_module) return false;
    if (formData.contentGenerationConfig.maxContentItemsPerModule !== s.max_content_items_per_module) return false;

    // Scenes and duration are checked only when the delivery type actually suggested them.
    if (s.min_scenes_per_content != null && s.max_scenes_per_content != null) {
      const scenes = (formData as any).sceneGenerationConfig;
      if (scenes?.minScenesPerContent !== s.min_scenes_per_content) return false;
      if (scenes?.maxScenesPerContent !== s.max_scenes_per_content) return false;
    }

    if (s.max_duration_seconds != null) {
      if ((formData as any).avatarConfig?.targetDurationSeconds !== s.max_duration_seconds) return false;
    }

    return true;
  })();

  // Helper function for setting content delivery type
  const setContentDeliveryType = (type: 'html' | 'html_with_audio' | 'video_presentation' | 'avatar_video') => {
    setValue('contentDeliveryType', type);
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Compact Hero Section */}
        <div className="text-center mb-6 bg-gradient-to-br from-primary-50 via-white to-brand-purple-50 rounded-xl py-4 -mx-4 sm:mx-0">
          <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-500 to-brand-purple-600 rounded-xl mb-3 shadow-lg">
            <Sparkles className="w-5 h-5 text-white animate-pulse" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-primary-600 to-brand-purple-600 bg-clip-text text-transparent">
            Create Your AI Course
          </h1>
          <p className="text-sm text-gray-700 mb-3 max-w-xl mx-auto px-4">
            Transform your ideas into comprehensive learning experiences with AI
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              <span>AI-Powered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
              <span>Multi-Language</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" />
              <span>Instant</span>
            </div>
          </div>
        </div>

        {/* Enhanced Main Form */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 max-w-4xl mx-auto">
          <form onSubmit={onSubmit} className="space-y-8">
            {/* Course Idea Section */}
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="mb-4 sm:mb-6">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <label className="block text-base sm:text-lg font-semibold text-gray-900 mb-2">
                      What course would you like to create?
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Describe your course idea in detail. Be specific about the topic, learning objectives, and target audience.
                      <span className="font-medium text-gray-800 block sm:inline"> (Minimum 100 characters required)</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Undo Button - Show when we have original text */}
                    {originalTextBeforeEnhancement && (
                      <button
                        type="button"
                        onClick={() => {
                          setValue('courseIdea', originalTextBeforeEnhancement, { shouldValidate: true });
                          setOriginalTextBeforeEnhancement('');
                          toast.success('Reverted to original text', {
                            icon: '↶',
                            duration: 2000
                          });
                        }}
                        className="p-2.5 sm:p-3 rounded-xl bg-gray-200 hover:bg-gray-300 transition-all transform hover:scale-110"
                        title="Undo enhancement"
                      >
                        <Undo2 className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                      </button>
                    )}

                    {/* AI Enhancement Button */}
                    <button
                      type="button"
                      onClick={async () => {
                        const currentText = formData.courseIdea.trim();
                        if (!currentText || currentText.length < 10) {
                          toast.error('Please enter at least 10 characters to enhance');
                          return;
                        }

                        setIsEnhancing(true);
                        const loadingToast = toast.loading('Enhancing your course idea with AI...');

                        try {
                          const result = await courseService.enhanceUserInput({
                            user_course_idea: currentText,
                            targeted_audience: formData.targetAudience || undefined,
                            difficulty_level: formData.difficultyLevel || undefined,
                          });

                          setOriginalTextBeforeEnhancement(currentText);
                          setValue('courseIdea', result.enhanced_input, { shouldValidate: true });
                          toast.dismiss(loadingToast);
                          toast.success('Course idea enhanced successfully!');
                        } catch (error: any) {
                          toast.dismiss(loadingToast);
                          toast.error(
                            error?.response?.data?.error?.error_description ||
                            error?.response?.data?.message ||
                            error?.message ||
                            'Failed to enhance input'
                          );
                          console.error(error);
                        } finally {
                          setIsEnhancing(false);
                        }
                      }}
                      disabled={!canWrite || isEnhancing || !formData.courseIdea.trim() || formData.courseIdea.trim().length < 10}
                      title={!canWrite ? 'You do not have permission to generate courses' : undefined}
                      className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-brand-purple-600 to-brand-pink-600 text-white rounded-lg hover:from-brand-purple-700 hover:to-brand-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                    >
                      {isEnhancing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enhancing...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4" />
                          Enhance with AI
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {/* Course idea - the shared Textarea adds the clean-text check and shows its error. */}
              <Textarea
                name="courseIdea"
                control={control}
                ref={textareaRef}
                rules={{
                  required: 'Please enter a course idea',
                  minLength: {
                    value: 100,
                    message: 'Course idea must be at least 100 characters'
                  }
                }}
                unstyled
                placeholder="Example: 'A comprehensive course on digital marketing fundamentals for small business owners. Cover topics like social media marketing, SEO basics, email marketing, and paid advertising strategies. Include practical examples and real-world case studies.'"
                className={`w-full min-h-[120px] max-h-[300px] resize-none border rounded-lg sm:rounded-xl p-4 sm:p-6 pb-10 sm:pb-12 text-sm sm:text-base text-gray-900 leading-relaxed bg-white focus:ring-2 focus:ring-primary-500 transition-all duration-200 placeholder:text-gray-300 placeholder:text-xs sm:placeholder:text-sm ${
                  errors.courseIdea ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
                }`}
                rows={4}
                overlay={
                  <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 text-xs bg-white px-2 py-1 rounded-md border border-gray-200">
                    <span className={formData.courseIdea.length < 100 ? 'text-red-500 font-medium' : 'text-gray-500'}>
                      {formData.courseIdea.length}
                    </span>
                    <span className="text-gray-500">/1000</span>
                    {formData.courseIdea.length < 100 && (
                      <span className="text-red-500 ml-1 sm:ml-2 text-[10px] sm:text-xs">({100 - formData.courseIdea.length} more)</span>
                    )}
                  </div>
                }
              />

              {/* Target Audience */}
              <div className="space-y-2 sm:space-y-3 mt-4 sm:mt-6">
                <label className="block text-sm sm:text-base font-semibold text-gray-900">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <p className="text-xs sm:text-sm text-gray-600 mb-2">
                  Who is this course designed for? Be specific about their background and skill level.
                </p>
                <Input
                  name="targetAudience"
                  control={control}
                  type="text"
                  rules={{
                    required: 'Please specify the target audience',
                    minLength: {
                      value: 3,
                      message: 'Target audience must be at least 3 characters'
                    }
                  }}
                  placeholder="e.g., Beginners with no prior experience, Marketing professionals, Software developers"
                  className="placeholder:text-gray-300"
                />
              </div>

              {/* Knowledge Store Integration */}
              <div className="space-y-3 sm:space-y-4 mt-4 sm:mt-6">
                <label className="block text-sm sm:text-base font-semibold text-gray-900">
                  Knowledge Store Integration
                </label>
                <p className="text-xs sm:text-sm text-gray-600">
                  Use your document collections to generate course content based on your specific materials
                </p>

                <div className="space-y-3">
                  {/* Knowledge Store Selection */}
                  <Select
                    name="knowledgeStoreId"
                    control={control}
                    label="Select Knowledge Store"
                    error={errors.knowledgeStoreId?.message}
                    options={[
                      { label: 'None - Use general AI knowledge', value: '' },
                      ...(documentStoresData?.map((store: any) => ({
                        label: store.name,
                        value: store._id
                      })) || [])
                    ]}
                    placeholder="Choose a knowledge store..."
                  />

                  {/* Knowledge Store Options - Only show when a store is selected */}
                  {watch('knowledgeStoreId') && (
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      {/* Knowledge Store Only Mode */}
                      <Toggle
                        name="knowledgeStoreOnlyMode"
                        control={control}
                        label="Knowledge Store Only Mode"
                        description="Use ONLY your knowledge store content, disable web search and general AI knowledge"
                      />

                      {/* Example Injection */}
                      <Toggle
                        name="exampleInjectionEnabled"
                        control={control}
                        label="Example Document Injection"
                        description="Include example documents from your knowledge store in the course content"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Advanced Settings Toggle */}
            <div className="border-t border-gray-100">
              <button 
                type="button" 
                onClick={() => setShowSettings(!showSettings)} 
                className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-5 xl:py-6 flex items-center justify-between text-xs sm:text-sm lg:text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <span className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-3">
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" />
                    <span>Advanced Settings</span>
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500 font-normal ml-6 sm:ml-0">
                    {showSettings ? 'Hide' : 'Customize options'}
                  </span>
                </span>
                {showSettings ? 
                  <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                }
              </button>
              {showSettings && (
                <div className="px-4 sm:px-8 pb-4 sm:pb-8 pt-4 space-y-4 sm:space-y-6 bg-gradient-to-r from-gray-50 to-primary-50 border-t border-gray-100">
                  {/* Course Settings */}
                  <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary-600" />
                      Course Settings
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                      {/* Difficulty Level */}
                      <Select
                        name="difficultyLevel"
                        control={control}
                        label="Difficulty Level"
                        error={errors.difficultyLevel?.message}
                        options={[
                          { label: 'Beginner - No prior knowledge needed', value: DifficultyLevel.BEGINNER },
                          { label: 'Intermediate - Some experience helpful', value: DifficultyLevel.INTERMEDIATE },
                          { label: 'Advanced - In-depth understanding required', value: DifficultyLevel.ADVANCED },
                          { label: 'Expert - Professional level mastery', value: DifficultyLevel.EXPERT }
                        ]}
                      />

                      {/* Tone */}
                      
                      <Select
                        name="tone"
                        control={control}
                        label="Content Tone"
                        error={errors.tone?.message}
                        options={[
                          { label: 'Formal - Academic and structured', value: Tone.FORMAL },
                          { label: 'Professional - Business-appropriate', value: Tone.PROFESSIONAL },
                          { label: 'Informal - Casual and approachable', value: Tone.INFORMAL },
                          { label: 'Friendly - Warm and welcoming', value: Tone.FRIENDLY },
                          { label: 'Persuasive - Compelling and convincing', value: Tone.PERSUASIVE },
                          { label: 'Inspirational - Motivating and uplifting', value: Tone.INSPIRATIONAL },
                          { label: 'Neutral - Balanced and objective', value: Tone.NEUTRAL },
                          { label: 'Educational - Clear and instructive', value: Tone.EDUCATIONAL },
                          { label: 'Empathetic - Understanding and supportive', value: Tone.EMPATHETIC },
                          { label: 'Playful - Fun and lighthearted', value: Tone.PLAYFUL }
                        ]}
                      />

                      {/* Pedagogy Framework */}
                      <Select
                        name="pedagogyFramework"
                        control={control}
                        label="Pedagogy Framework"
                        error={errors.pedagogyFramework?.message}
                        options={[
                          { label: 'Default - Standard course structure', value: 'DEFAULT' },
                          { label: 'ADDIE - Analysis, Design, Develop, Implement, Evaluate', value: 'ADDIE' },
                          { label: "Bloom's Taxonomy - Hierarchical learning objectives", value: 'BLOOMS_TAXONOMY' },
                          { label: "Gagné's 9 Events - Systematic instructional design", value: 'GAGNES_NINE_EVENTS' },
                          { label: 'Kolb’s Experiential - Learning through experience cycle', value: 'KOLBS_EXPERIENTIAL' },
                          { label: 'Constructivist - Learner-centered knowledge building', value: 'CONSTRUCTIVIST' },
                          { label: 'Problem-Based - Learning through real-world problem solving', value: 'PROBLEM_BASED' },
                          { label: 'Flipped Classroom - Reverse traditional learning sequence', value: 'FLIPPED_CLASSROOM' },
                          { label: 'Microlearning - Bite-sized learning for retention', value: 'MICROLEARNING' },
                          { label: 'Competency-Based - Mastery-driven learning outcomes', value: 'COMPETENCY_BASED' },
                          { label: 'Adaptive Learning - Personalized and data-driven learning path', value: 'ADAPTIVE_LEARNING' }
                        ]}
                      />
                    </div>
                  </div>

                  {/* Course Structure */}
                  <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Target className="w-4 h-4 text-brand-blue-600" />
                      Course Structure
                    </h3>

                    <div className="space-y-4">
                      {/* Prerequisites - the shared TagsField adds the clean-text check to each entry. */}
                      <TagsField
                        label="Prerequisites"
                        value={formData.prerequisites}
                        onChange={(tags) => setValue('prerequisites', tags)}
                        placeholder="Add prerequisite (e.g., Basic JavaScript knowledge)"
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Industry */}
                        <div className="space-y-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700">
                            <Building className="w-3 h-3 inline mr-1" />
                            Target Industry
                          </label>
                          <Input
                            name="industry"
                            control={control}
                            type="text"
                            placeholder="e.g., Technology, Healthcare, Finance"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Language Settings Section */}
                  <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Languages className="w-4 h-4 text-primary-600" />
                      Language Settings
                    </h3>
                    
                    <div className="space-y-4">
                      {/* ---------------------------------------------------------
                          "Generate in X" / "Then translate into Y, Z" reads as one
                          sentence. The word "Then" carries the ordering, so no help
                          text is needed to explain that the master is produced first
                          and published before any translation starts.
                         --------------------------------------------------------- */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Language Code */}
                        <Select
                          name="languageCode"
                          control={control}
                          label="Generate in"
                          error={errors.languageCode?.message}
                          options={languageOptions}
                        />

                        {/* Same component as "Generate in" — matching the control
                            matters more than saving a click. A different-looking
                            picker for a neighbouring field reads as a different
                            KIND of setting. */}
                        <Select
                          name={'translationPicker' as any}
                          control={control}
                          label="Then translate into"
                          placeholder="Add a language"
                          closeMenuOnSelect={false}
                          options={languageOptions
                            // Cannot translate a language into itself, and one
                            // already chosen is not offered twice. Both are
                            // rejected by the API — cheaper to prevent here.
                            .filter((o) => o.value !== formData.languageCode)
                            .filter(
                              (o) =>
                                !(formData.translationLanguages ?? []).some(
                                  (l) => l.language_code === o.value
                                )
                            )}
                          // Disabled when nothing is left to pick, or when the course
                          // is an avatar video — that presenter is generated by an
                          // external provider, which sets its own language.
                          isDisabled={
                            formData.contentDeliveryType === 'avatar_video' ||
                            languageOptions.filter(
                              (o) =>
                                o.value !== formData.languageCode &&
                                !(formData.translationLanguages ?? []).some(
                                  (l) => l.language_code === o.value
                                )
                            ).length === 0
                          }
                        />
                      </div>

                      {formData.contentDeliveryType === 'avatar_video' && (
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                          Avatar Video courses cannot be translated. The presenter is generated by
                          an external provider, which sets its own language and voice.
                        </p>
                      )}

                      {/* Chosen languages ---------------------------------------- */}
                      <div className="space-y-2">
                        {(formData.translationLanguages?.length ?? 0) === 0 ? (
                          <p className="text-xs text-gray-400">
                            No translations. The course will be produced in{' '}
                            {languageOptions.find((o) => o.value === formData.languageCode)?.label ??
                              'the selected language'}{' '}
                            only.
                          </p>
                        ) : (
                          <>
                            <div className="flex flex-wrap gap-2">
                              {formData.translationLanguages.map((lang) => (
                                <span
                                  key={lang.language_code}
                                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-brand-indigo-50 border border-brand-indigo-200 text-xs font-medium text-brand-indigo-700"
                                >
                                  {lang.language_name}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setValue(
                                        'translationLanguages' as any,
                                        formData.translationLanguages.filter(
                                          (l) => l.language_code !== lang.language_code
                                        )
                                      )
                                    }
                                    className="p-0.5 rounded-full hover:bg-brand-indigo-100 transition-colors"
                                    aria-label={`Remove ${lang.language_name}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>

                            <div className="text-[11px] text-gray-500 space-y-0.5">
                              <p>
                                {languageOptions.find((o) => o.value === formData.languageCode)?.label ??
                                  'The course'}{' '}
                                is generated first and published as soon as it&apos;s ready. Each
                                translation follows and appears when complete.
                              </p>
                            </div>
                          </>
                        )}

                        {/* The old copy here announced a cap of 5. There is no cap
                            now, so the note says the thing that is actually true and
                            actually useful: they run one after another, and the
                            course does not wait for them. */}
                        {(formData.translationLanguages?.length ?? 0) > 1 && (
                          <p className="text-[11px] text-gray-500">
                            {formData.translationLanguages.length} languages — they run one
                            after another, and each appears as soon as it finishes. The
                            course itself publishes without waiting for them.
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Device Type */}
                        {/* <div className="space-y-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700">
                            Target Device
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setValue('deviceType', 'mobile')}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                formData.deviceType === 'mobile'
                                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <Smartphone className="w-4 h-4" />
                              <span className="text-xs">Mobile</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setValue('deviceType', 'desktop')}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                formData.deviceType === 'desktop'
                                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <Monitor className="w-4 h-4" />
                              <span className="text-xs">Desktop</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setValue('deviceType', 'tablet')}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                formData.deviceType === 'tablet'
                                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <Tablet className="w-4 h-4" />
                              <span className="text-xs">Tablet</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setValue('deviceType', 'speaker')}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                                formData.deviceType === 'speaker'
                                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <Speaker className="w-4 h-4" />
                              <span className="text-xs">Speaker</span>
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">
                            Optimizes audio quality for the target device
                          </p>
                        </div> */}
                      </div>

                      {/* Language Info Alert */}
                      {/* <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Globe className="w-4 h-4 text-blue-600 mt-0.5" />
                          <div className="text-xs text-blue-800">
                            <p className="font-semibold mb-1">Language Configuration Active</p>
                            <p>Using advanced TTS/STT configuration for {formData.languageCode} optimized for {formData.deviceType} devices.</p>
                            <p className="mt-1">Voice: {formData.ttsConfig.voiceType} • Rate: {formData.ttsConfig.speakingRate}x • Captions: {formData.captionConfig.enableCaptions ? 'Enabled' : 'Disabled'}</p>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </div>

                  {/* Content & Module Configuration */}
                  <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Video className="w-4 h-4 text-brand-purple-600" />
                      Content & Module Configuration
                    </h3>

                    <div className="space-y-4">
                      {/* Content Delivery Type */}
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700">
                          Content Delivery Type
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                          {(() => {
                            // Avatar video enablement is per-tenant, resolved by the backend
                            // (GET /course/capabilities) from tenant config — no longer a
                            // build-time hostname env var.
                            const avatar_video_enabled = avatarVideoEnabled;

                            // An avatar presenter is produced by an external provider that
                            // sets its own language, so it cannot be translated.
                            const translating = (formData.translationLanguages?.length ?? 0) > 0;
                            const avatar_blocked = avatar_video_enabled && translating;

                            return [
                              { key: 'html' as const, label: 'HTML', description: 'Interactive HTML slides', icon: FileText, disabled: false, coming_soon: false },
                              { key: 'html_with_audio' as const, label: 'HTML + Audio', description: 'HTML slides with narration', icon: Volume2, disabled: false, coming_soon: false },
                              { key: 'video_presentation' as const, label: 'Video', description: 'Video with HTML slides', icon: Video, disabled: false, coming_soon: false },
                              {
                                key: 'avatar_video' as const,
                                label: 'Avatar Video',
                                description: avatar_blocked
                                  ? 'Not available with translation'
                                  : avatar_video_enabled ? 'AI avatar talking head' : 'Coming soon',
                                icon: User,
                                disabled: !avatar_video_enabled || avatar_blocked,
                                coming_soon: !avatar_video_enabled,
                                blocked_reason: avatar_blocked
                                  ? 'Avatar videos cannot be translated — the presenter is generated by an external provider, which sets its own language. Remove the languages above to choose Avatar Video.'
                                  : '',
                              },
                            ].map(({ key, label, description, icon: Icon, disabled, coming_soon, blocked_reason }: any) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => !disabled && setContentDeliveryType(key)}
                                disabled={disabled}
                                title={blocked_reason || (coming_soon ? 'Coming soon — contact sales for early access' : '')}
                                className={`flex items-center gap-3 px-3 py-3 text-xs rounded-lg border transition-all relative ${
                                  disabled
                                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-70'
                                    : formData.contentDeliveryType === key
                                      ? 'bg-brand-purple-50 border-brand-purple-300 text-brand-purple-700 ring-1 ring-brand-purple-300'
                                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                <div className="text-left flex-1">
                                  <div className="font-medium flex items-center gap-1.5">
                                    {label}
                                    {coming_soon && (
                                      <span className="px-1.5 py-0.5 text-[8px] font-semibold bg-amber-100 text-amber-700 rounded uppercase">
                                        Soon
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-gray-500">{description}</div>
                                </div>
                              </button>
                            ));
                          })()}
                        </div>
                        {(formData.translationLanguages?.length ?? 0) > 0 && avatarVideoEnabled && (
                          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mt-2">
                            Avatar Video is unavailable while languages are selected — an avatar
                            presenter is generated by an external provider, which sets its own
                            language. Remove the languages to use it.
                          </p>
                        )}
                      </div>

                      {/* What the selected knowledge store can support as a course. Placed after
                          the delivery type because the type decides whether the material is
                          measured in scenes, in video length, or in neither. */}
                      {formData.knowledgeStoreId && (
                        <KnowledgeStoreAnalysisPanel
                          knowledgeStoreId={formData.knowledgeStoreId}
                          deliveryType={formData.contentDeliveryType}
                          limits={analysisLimits}
                          onApply={applyAnalyzedStructure}
                          onAnalysis={setAnalyzedSuggestion}
                          isApplied={isSuggestionApplied}
                        />
                      )}

                      {/* Theme Selector — not relevant for avatar_video (no HTML slides) */}
                      {formData.contentDeliveryType !== 'avatar_video' && (
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700">
                          Design Theme
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          {[
                            { key: 'educational', label: 'Educational', desc: 'Warm & approachable', color: '#3b82f6', secondary: '#10b981', bg: '#f8fafc', panelBg: '#ffffff', radius: '12px', accentType: 'gradient' },
                            { key: 'modern', label: 'Modern', desc: 'Sleek & tech-forward', color: '#8b5cf6', secondary: '#06b6d4', bg: '#faf8ff', panelBg: '#faf8ff', radius: '16px', accentType: 'gradient' },
                            { key: 'corporate', label: 'Corporate', desc: 'Professional & structured', color: '#059669', secondary: '#0891b2', bg: '#f9fafb', panelBg: '#f9fafb', radius: '4px', accentType: 'solid' },
                            { key: 'dark', label: 'Dark', desc: 'High contrast & bold', color: '#60a5fa', secondary: '#34d399', bg: '#1f2937', panelBg: '#374151', radius: '8px', accentType: 'gradient' },
                            { key: 'minimal', label: 'Minimal', desc: 'Clean & typography-driven', color: '#6b7280', secondary: '#9ca3af', bg: '#ffffff', panelBg: 'transparent', radius: '0px', accentType: 'line' },
                          ].map(({ key, label, desc, color, secondary, bg, panelBg, radius, accentType }) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setValue('htmlSlidesConfig.theme' as any, key)}
                              className={`flex flex-col items-center gap-1 px-2 py-2 text-xs rounded-lg border transition-all ${
                                formData.htmlSlidesConfig.theme === key
                                  ? 'bg-brand-purple-50 border-brand-purple-300 text-brand-purple-700 ring-1 ring-brand-purple-300'
                                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {/* Mini slide preview */}
                              <div
                                className="w-full h-10 rounded overflow-hidden relative"
                                style={{ background: bg, border: key === 'dark' ? 'none' : '1px solid #e5e7eb' }}
                              >
                                {/* Accent line */}
                                <div
                                  className="absolute top-0 left-0 w-full"
                                  style={{
                                    height: accentType === 'line' ? 1 : accentType === 'solid' ? 2 : 3,
                                    background: accentType === 'gradient'
                                      ? `linear-gradient(90deg, ${color}, ${secondary})`
                                      : color,
                                  }}
                                />
                                {/* Mini panel */}
                                <div
                                  className="absolute"
                                  style={{
                                    top: 8,
                                    left: 6,
                                    right: 6,
                                    bottom: 4,
                                    background: panelBg,
                                    borderRadius: radius,
                                    boxShadow: panelBg === 'transparent' ? 'none' : '0 0 3px rgba(0,0,0,0.06)',
                                  }}
                                >
                                  {/* Mini text lines */}
                                  <div className="px-1.5 pt-1.5">
                                    <div style={{ height: 3, width: '60%', borderRadius: 1, background: key === 'dark' ? '#f9fafb' : '#374151', opacity: 0.7 }} />
                                    <div style={{ height: 2, width: '85%', borderRadius: 1, background: key === 'dark' ? '#9ca3af' : '#9ca3af', marginTop: 3, opacity: 0.4 }} />
                                    <div style={{ height: 2, width: '70%', borderRadius: 1, background: key === 'dark' ? '#9ca3af' : '#9ca3af', marginTop: 2, opacity: 0.3 }} />
                                  </div>
                                </div>
                              </div>
                              <span className="font-medium text-[11px]">{label}</span>
                              <span className="text-[9px] text-gray-400 leading-tight">{desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      )}

                      {/* Avatar Video Configuration - only for avatar_video */}
                      {formData.contentDeliveryType === 'avatar_video' && (
                        <div className="p-3 bg-brand-purple-50/50 rounded-lg space-y-3 border border-brand-purple-100">
                          <h4 className="text-xs font-semibold text-brand-purple-800 flex items-center gap-2">
                            <User className="w-3.5 h-3.5" />
                            Avatar Configuration
                          </h4>

                          {/* Provider is fixed to HeyGen — D-ID hidden from UI until it's productized */}

                          {/* Video Mode — hidden for now; full_animated_video is the only supported mode.
                              Re-enable next month via ENABLE_TALKING_HEAD_CONTROLS. */}
                          {ENABLE_TALKING_HEAD_CONTROLS && (
                            <div className="space-y-1.5">
                              <label className="block text-[11px] font-medium text-gray-600">Video Mode</label>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { key: 'talking_head' as const, label: 'Talking Head', desc: 'Avatar speaks over slides' },
                                  { key: 'full_animated_video' as const, label: 'Full Animated', desc: 'HeyGen generates entire video', heygenOnly: true },
                                ].map(({ key, label, desc, heygenOnly }) => {
                                  const disabled = heygenOnly && formData.avatarConfig.provider !== 'heygen';
                                  return (
                                    <button
                                      key={key}
                                      type="button"
                                      onClick={() => !disabled && setValue('avatarConfig.videoMode' as any, key)}
                                      disabled={disabled}
                                      className={`px-3 py-2 text-xs rounded-lg border transition-all text-left ${
                                        disabled
                                          ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                          : formData.avatarConfig.videoMode === key
                                            ? 'bg-brand-purple-50 border-brand-purple-300 text-brand-purple-700 ring-1 ring-brand-purple-300'
                                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                      }`}
                                    >
                                      <div className="font-medium">{label}</div>
                                      <div className="text-[10px] text-gray-500">{desc}</div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Engine — only for talking_head (full_animated uses Video Agent which picks its own). Hidden for now. */}
                          {ENABLE_TALKING_HEAD_CONTROLS && formData.avatarConfig.videoMode === 'talking_head' && (
                            <div className="space-y-1.5">
                              <label className="block text-[11px] font-medium text-gray-600">Engine</label>
                              <ReactSelect
                                value={{ value: formData.avatarConfig.engine, label: { 'avatar_3': 'Avatar III — $0.017/sec (cheapest)', 'avatar_4': 'Avatar IV — $0.067/sec (better quality)' }[formData.avatarConfig.engine] || 'Avatar IV' }}
                                onChange={(option: any) => setValue('avatarConfig.engine' as any, option?.value || 'avatar_4')}
                                options={[
                                  { value: 'avatar_3', label: 'Avatar III — $0.017/sec (cheapest)' },
                                  { value: 'avatar_4', label: 'Avatar IV — $0.067/sec (better quality)' },
                                ]}
                                styles={voiceSelectStyles}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                              />
                            </div>
                          )}

                          {/* Resolution — only for talking_head + Avatar IV (Avatar III is hardcoded 1080p; Video Agent picks its own). Hidden for now. */}
                          {ENABLE_TALKING_HEAD_CONTROLS && formData.avatarConfig.videoMode === 'talking_head' && formData.avatarConfig.engine === 'avatar_4' && (
                            <div className="space-y-1.5">
                              <label className="block text-[11px] font-medium text-gray-600">Resolution</label>
                              <ReactSelect
                                value={{ value: formData.avatarConfig.resolution, label: { '720p': '720p — Lowest cost', '1080p': '1080p — HD', '4k': '4K — Highest quality' }[formData.avatarConfig.resolution] || '720p' }}
                                onChange={(option: any) => setValue('avatarConfig.resolution' as any, option?.value || '720p')}
                                options={[
                                  { value: '720p', label: '720p — Lowest cost' },
                                  { value: '1080p', label: '1080p — HD' },
                                  { value: '4k', label: '4K — Highest quality' },
                                ]}
                                styles={voiceSelectStyles}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                              />
                            </div>
                          )}

                          {/* Target Duration — only for Video Agent (full_animated_video).
                              Drives narration word count and the HeyGen duration hint. */}
                          {formData.avatarConfig.videoMode === 'full_animated_video' && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <label className="block text-[11px] font-medium text-gray-600">
                                  Target Duration: {Math.round(formData.avatarConfig.targetDurationSeconds / 60 * 10) / 10} min
                                  <span className="ml-1 text-gray-400">({formData.avatarConfig.targetDurationSeconds}s)</span>
                                </label>
                                <span className="text-[10px] text-brand-purple-700 font-medium">
                                  Est. ~${(formData.avatarConfig.targetDurationSeconds * 0.0333).toFixed(2)}
                                </span>
                              </div>
                              <input
                                type="range"
                                min={60}
                                max={360}
                                step={30}
                                value={formData.avatarConfig.targetDurationSeconds}
                                onChange={(e) => setValue('avatarConfig.targetDurationSeconds' as any, Number(e.target.value))}
                                className="w-full accent-brand-purple-600"
                              />
                              <p className="text-[10px] text-gray-400">
                                HeyGen will aim for this length. Actual output may vary by ±30s depending on narration pacing.
                              </p>
                            </div>
                          )}

                          {/* HeyGen Voice Picker - only for full_animated_video */}
                          {formData.avatarConfig.videoMode === 'full_animated_video' && formData.avatarConfig.provider === 'heygen' && (
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <label className="block text-[11px] font-medium text-gray-600">HeyGen Voice</label>
                                {isLoadingVoices && (
                                  <Loader2 className="w-3 h-3 animate-spin text-brand-purple-500" />
                                )}
                              </div>
                              <p className="text-[10px] text-gray-400">
                                HeyGen will generate both voice and video.
                                {formData.languageCode && LOCALE_TO_HEYGEN_LANGUAGE[formData.languageCode]
                                  ? ` Showing voices for ${LOCALE_TO_HEYGEN_LANGUAGE[formData.languageCode]}.`
                                  : ' Select a course language to filter voices.'}
                              </p>
                              <ReactSelect<VoiceSelectOption>
                                value={
                                  formData.avatarConfig.voiceId
                                    ? {
                                        value: formData.avatarConfig.voiceId,
                                        label: avatarVoices.find(v => v.voice_id === formData.avatarConfig.voiceId)
                                          ? `${avatarVoices.find(v => v.voice_id === formData.avatarConfig.voiceId)!.name} — ${avatarVoices.find(v => v.voice_id === formData.avatarConfig.voiceId)!.language} (${avatarVoices.find(v => v.voice_id === formData.avatarConfig.voiceId)!.gender})`
                                          : formData.avatarConfig.voiceId,
                                        voice: avatarVoices.find(v => v.voice_id === formData.avatarConfig.voiceId)!,
                                      }
                                    : null
                                }
                                onChange={(option) => setValue('avatarConfig.voiceId' as any, option?.value || '')}
                                options={avatarVoices.map(v => ({
                                  value: v.voice_id,
                                  label: `${v.name} — ${v.language} (${v.gender})`,
                                  voice: v,
                                }))}
                                placeholder="Search voices... (leave empty for Google TTS)"
                                isClearable
                                isSearchable
                                isLoading={isLoadingVoices}
                                styles={voiceSelectStyles}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                maxMenuHeight={200}
                                noOptionsMessage={() =>
                                  isLoadingVoices ? 'Loading voices...' : 'No voices found for this language'
                                }
                                formatOptionLabel={(option) => (
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      {option.voice?.preview_audio_url && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (previewingVoiceId === option.value) {
                                              voicePreviewRef.current?.pause();
                                              setPreviewingVoiceId(null);
                                            } else {
                                              if (voicePreviewRef.current) voicePreviewRef.current.pause();
                                              const audio = new Audio(option.voice!.preview_audio_url!);
                                              audio.onended = () => setPreviewingVoiceId(null);
                                              audio.play();
                                              voicePreviewRef.current = audio;
                                              setPreviewingVoiceId(option.value);
                                            }
                                          }}
                                          className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-purple-100 hover:bg-brand-purple-200 flex items-center justify-center transition-colors"
                                        >
                                          {previewingVoiceId === option.value
                                            ? <XCircle className="w-3 h-3 text-brand-purple-600" />
                                            : <Volume2 className="w-3 h-3 text-brand-purple-600" />}
                                        </button>
                                      )}
                                      <span className="truncate">{option.voice?.name || option.value}</span>
                                    </div>
                                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                                      {option.voice?.language} · {option.voice?.gender}
                                    </span>
                                  </div>
                                )}
                              />
                              {formData.avatarConfig.voiceId ? (
                                <p className="text-[10px] text-amber-600">
                                  HeyGen TTS will be used instead of Google TTS for this course.
                                </p>
                              ) : (
                                <p className="text-[10px] text-gray-400">
                                  Leave empty to use Google TTS audio (your existing voice settings).
                                </p>
                              )}
                            </div>
                          )}

                          {/* AI Video Agent info - full_animated always uses Video Agent */}
                          {formData.avatarConfig.videoMode === 'full_animated_video' && formData.avatarConfig.provider === 'heygen' && (
                            <div className="p-2.5 bg-brand-purple-50/50 rounded-lg border border-brand-purple-100">
                              <p className="text-[10px] text-brand-purple-700">
                                <Sparkles className="w-3 h-3 inline mr-1" />
                                HeyGen AI Video Agent will generate a rich, AI-composed video from the course content. No scene splitting needed — one video per content item.
                              </p>
                            </div>
                          )}

                          {/* Composition Mode — hidden for now (full-animated only). Re-enable next month via ENABLE_TALKING_HEAD_CONTROLS. */}
                          {ENABLE_TALKING_HEAD_CONTROLS && formData.avatarConfig.videoMode === 'talking_head' && (
                            <div className="space-y-1.5">
                              <label className="block text-[11px] font-medium text-gray-600">Composition Mode</label>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { key: 'overlay' as const, label: 'Overlay (PiP)', desc: 'Avatar over slide corner' },
                                  { key: 'fullscreen' as const, label: 'Fullscreen', desc: 'Avatar only, no slides' },
                                ].map(({ key, label, desc }) => (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={() => setValue('avatarConfig.compositionMode' as any, key)}
                                    className={`px-3 py-2 text-xs rounded-lg border transition-all text-left ${
                                      formData.avatarConfig.compositionMode === key
                                        ? 'bg-brand-purple-50 border-brand-purple-300 text-brand-purple-700 ring-1 ring-brand-purple-300'
                                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div className="font-medium">{label}</div>
                                    <div className="text-[10px] text-gray-500">{desc}</div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* PiP Settings (position / shape / size / border) — hidden for now. Re-enable next month via ENABLE_TALKING_HEAD_CONTROLS. */}
                          {ENABLE_TALKING_HEAD_CONTROLS && formData.avatarConfig.videoMode === 'talking_head' && formData.avatarConfig.compositionMode === 'overlay' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1.5">
                                <label className="block text-[11px] font-medium text-gray-600">Position</label>
                                <ReactSelect
                                  value={{ value: formData.avatarConfig.position, label: { 'bottom_right': 'Bottom Right', 'bottom_left': 'Bottom Left', 'top_right': 'Top Right', 'top_left': 'Top Left' }[formData.avatarConfig.position] || 'Bottom Right' }}
                                  onChange={(option: any) => setValue('avatarConfig.position' as any, option?.value || 'bottom_right')}
                                  options={[
                                    { value: 'bottom_right', label: 'Bottom Right' },
                                    { value: 'bottom_left', label: 'Bottom Left' },
                                    { value: 'top_right', label: 'Top Right' },
                                    { value: 'top_left', label: 'Top Left' },
                                  ]}
                                  styles={voiceSelectStyles}
                                  menuPortalTarget={document.body}
                                  isSearchable={false}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="block text-[11px] font-medium text-gray-600">Shape</label>
                                <ReactSelect
                                  value={{ value: formData.avatarConfig.pipShape, label: { 'circle': 'Circle', 'rounded_rect': 'Rounded Rectangle' }[formData.avatarConfig.pipShape] || 'Circle' }}
                                  onChange={(option: any) => setValue('avatarConfig.pipShape' as any, option?.value || 'circle')}
                                  options={[
                                    { value: 'circle', label: 'Circle' },
                                    { value: 'rounded_rect', label: 'Rounded Rectangle' },
                                  ]}
                                  styles={voiceSelectStyles}
                                  menuPortalTarget={document.body}
                                  isSearchable={false}
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="block text-[11px] font-medium text-gray-600">
                                  Size: {formData.avatarConfig.pipSizePercent}%
                                </label>
                                <input
                                  type="range"
                                  min={15}
                                  max={50}
                                  value={formData.avatarConfig.pipSizePercent}
                                  onChange={e => setValue('avatarConfig.pipSizePercent' as any, Number(e.target.value))}
                                  className="w-full accent-brand-purple-600"
                                />
                              </div>
                              <div className="space-y-1.5 flex items-center gap-2 pt-4">
                                <input
                                  type="checkbox"
                                  checked={formData.avatarConfig.pipBorder}
                                  onChange={e => setValue('avatarConfig.pipBorder' as any, e.target.checked)}
                                  className="rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500"
                                />
                                <label className="text-[11px] font-medium text-gray-600">Show Border</label>
                              </div>
                            </div>
                          )}

                          {/* Avatar Picker */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <label className="block text-[11px] font-medium text-gray-600">
                                Select Avatar <span className="text-red-500">*</span>
                              </label>
                              <button
                                type="button"
                                onClick={handleRefreshAvatars}
                                disabled={isRefreshingAvatars}
                                title="Refresh avatar catalog from HeyGen (cached daily — use this if you just added new avatars)"
                                className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-brand-purple-700 bg-brand-purple-50 border border-brand-purple-200 rounded hover:bg-brand-purple-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                              >
                                <RefreshCw className={`w-3 h-3 ${isRefreshingAvatars ? 'animate-spin' : ''}`} />
                                {isRefreshingAvatars ? 'Refreshing…' : 'Refresh'}
                              </button>
                            </div>

                            {/* Required: without an explicit avatar the provider auto-picks one from the content. */}
                            {!formData.avatarConfig.avatarId && (
                              <p className="text-[10px] text-amber-600">
                                Selecting an avatar is required — otherwise one is auto-chosen from the content.
                              </p>
                            )}

                            {/* Source toggle: All / Public stock / Private uploaded — hidden for now
                                (always "All"). Re-enable via ENABLE_AVATAR_SOURCE_FILTER when private avatars ship. */}
                            {ENABLE_AVATAR_SOURCE_FILTER && (
                            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-[10px] font-medium">
                              {([
                                { key: 'all', label: 'All' },
                                { key: 'heygen_stock', label: 'Public' },
                                { key: 'private_upload', label: 'Private' },
                              ] as const).map(opt => (
                                <button
                                  key={opt.key}
                                  type="button"
                                  onClick={() => setAvatarSourceFilter(opt.key)}
                                  className={`px-2.5 py-1 rounded-md transition-colors ${
                                    avatarSourceFilter === opt.key
                                      ? 'bg-white text-brand-purple-700 shadow-sm border border-brand-purple-200'
                                      : 'text-gray-500 hover:text-gray-700'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            )}

                            {/* Name search */}
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                              <input
                                type="text"
                                value={avatarSearch}
                                onChange={e => setAvatarSearch(e.target.value)}
                                placeholder="Search by name…"
                                className="w-full pl-7 pr-7 py-1.5 text-xs rounded-lg border border-gray-200 bg-white placeholder-gray-400 focus:ring-1 focus:ring-brand-purple-300 focus:border-brand-purple-300"
                              />
                              {avatarSearch && (
                                <button
                                  type="button"
                                  onClick={() => setAvatarSearch('')}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>

                            {/* Skeleton Loader */}
                            {isLoadingAvatars ? (
                              <div className="grid grid-cols-4 gap-2 p-1">
                                {Array.from({ length: 8 }).map((_, i) => (
                                  <div key={i} className="flex flex-col items-center gap-1 p-1.5 rounded-lg border border-gray-200">
                                    <div className="w-full aspect-square rounded-md bg-gray-200 animate-pulse" />
                                    <div className="h-2.5 w-3/4 bg-gray-200 animate-pulse rounded" />
                                    <div className="h-2 w-1/2 bg-gray-100 animate-pulse rounded" />
                                  </div>
                                ))}
                              </div>
                            ) : avatarModels.length > 0 ? (
                              <>
                                <div className="grid grid-cols-4 gap-2 p-1">
                                  {avatarModels.map((avatar) => {
                                    // Always show variations button for HeyGen — looks_count may be missing from API
                                    const has_variations = avatar.provider === 'heygen';
                                    const group_id = avatar.metadata?.group_id || avatar.id;
                                    // Selected if the base avatar is chosen OR a look/outfit from this group is chosen.
                                    const is_selected = formData.avatarConfig.avatarId === avatar.id
                                      || (!!selectedAvatarGroupId && selectedAvatarGroupId === group_id);
                                    return (
                                      <div
                                        key={`${avatar.provider}-${avatar.id}`}
                                        className={`group relative flex flex-col items-center gap-1 p-1.5 rounded-lg border transition-all cursor-pointer ${
                                          is_selected
                                            ? 'border-brand-purple-400 ring-2 ring-brand-purple-200 bg-brand-purple-50'
                                            : 'border-gray-200 hover:border-brand-purple-200 hover:bg-gray-50'
                                        }`}
                                        onClick={() => {
                                          setValue('avatarConfig.sourceUrl' as any, avatar.preview_image_url || avatar.id);
                                          setValue('avatarConfig.avatarId' as any, avatar.id);
                                          setSelectedAvatarGroupId(group_id);
                                        }}
                                      >
                                        <div className="w-full aspect-square rounded-md overflow-hidden bg-gray-100 relative">
                                          {avatar.preview_image_url ? (
                                            <img
                                              src={avatar.preview_image_url}
                                              alt={avatar.name}
                                              className="w-full h-full object-cover"
                                              loading="lazy"
                                              onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                              <User className="w-6 h-6 text-gray-400" />
                                            </div>
                                          )}
                                          {has_variations && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openLooksModal(avatar);
                                              }}
                                              className="absolute bottom-0.5 right-0.5 p-1 bg-brand-purple-600 text-white rounded shadow hover:bg-brand-purple-700 transition-colors"
                                              title="Choose outfit / background"
                                            >
                                              <Shirt className="w-3 h-3" />
                                            </button>
                                          )}
                                          {avatar.metadata?.preview_video_url && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const url = avatar.metadata?.preview_video_url as string;
                                                if (previewingAvatarId === avatar.id) {
                                                  avatarPreviewRef.current?.pause();
                                                  setPreviewingAvatarId(null);
                                                } else {
                                                  if (avatarPreviewRef.current) avatarPreviewRef.current.pause();
                                                  if (voicePreviewRef.current) voicePreviewRef.current.pause();
                                                  const audio = new Audio(url);
                                                  audio.onended = () => setPreviewingAvatarId(null);
                                                  audio.onerror = () => setPreviewingAvatarId(null);
                                                  audio.play().catch(() => setPreviewingAvatarId(null));
                                                  avatarPreviewRef.current = audio;
                                                  setPreviewingAvatarId(avatar.id);
                                                }
                                              }}
                                              className="absolute bottom-0.5 left-0.5 w-5 h-5 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow transition-colors"
                                              title="Preview default voice"
                                            >
                                              {previewingAvatarId === avatar.id
                                                ? <XCircle className="w-3 h-3 text-brand-purple-600" />
                                                : <Volume2 className="w-3 h-3 text-brand-purple-600" />}
                                            </button>
                                          )}
                                        </div>
                                        <span className="text-[9px] text-gray-600 truncate w-full text-center">{avatar.name}</span>
                                        <span className="text-[8px] text-gray-400 uppercase">{avatar.provider === 'd_id' ? 'D-ID' : 'HeyGen'}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                {avatarHasMore && (
                                  <button
                                    type="button"
                                    onClick={loadMoreAvatars}
                                    disabled={isLoadingMoreAvatars}
                                    className="w-full py-1.5 text-[10px] text-brand-purple-600 hover:text-brand-purple-800 bg-brand-purple-50 hover:bg-brand-purple-100 rounded-lg border border-brand-purple-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                                  >
                                    {isLoadingMoreAvatars ? (
                                      <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Loading more...
                                      </>
                                    ) : (
                                      'Show more avatars'
                                    )}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                                <User className="w-6 h-6 text-gray-300 mx-auto mb-1" />
                                <p className="text-[10px] text-gray-400">
                                  {!configuredProviders.d_id && !configuredProviders.heygen
                                    ? 'No avatar providers configured. Add API keys in tenant settings.'
                                    : 'No avatars found. Use a custom URL below.'}
                                </p>
                              </div>
                            )}

                          </div>
                        </div>
                      )}

                      {/* Video Rendering Mode - only for video_presentation */}
                      {formData.contentDeliveryType === 'video_presentation' && (
                        <div className="space-y-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700">
                            Video Rendering Mode
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { key: 'static', label: 'Static HTML', desc: 'HTML screenshot + audio overlay (fast render)', icon: '⚡', disabled: false },
                              { key: 'animated', label: 'Animated', desc: 'Rich animated slides with transitions (slower render)', icon: '✨', disabled: true },
                            ].map(({ key, label, desc, icon, disabled }) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => !disabled && setValue('htmlSlidesConfig.videoRenderingMode' as any, key)}
                                disabled={disabled}
                                className={`flex items-center gap-3 px-3 py-2.5 text-xs rounded-lg border transition-all text-left relative ${
                                  disabled
                                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                    : formData.htmlSlidesConfig.videoRenderingMode === key
                                      ? 'bg-brand-purple-50 border-brand-purple-300 text-brand-purple-700 ring-1 ring-brand-purple-300'
                                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                <span className="text-lg">{icon}</span>
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {label}
                                    {disabled && (
                                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-semibold rounded-full">Coming Soon</span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-gray-500">{desc}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* TTS Configuration - hidden for full_animated_video (Video Agent handles TTS) */}
                      {formData.contentDeliveryType !== 'html' && !(
                        formData.contentDeliveryType === 'avatar_video'
                        && formData.avatarConfig?.videoMode === 'full_animated_video'
                        && formData.avatarConfig?.provider === 'heygen'
                      ) && (
                      <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                        <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                          <Volume2 className="w-3 h-3 text-primary-600" />
                          Text-to-Speech Settings
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Select
                            name={'ttsConfig.voiceType' as any}
                            control={control}
                            label="Voice Type"
                            options={getVoiceTypesForLanguage(formData.languageCode).map((vt) => ({
                              value: vt.value,
                              label: vt.description ? `${vt.label} - ${vt.description}` : vt.label,
                            }))}
                            isSearchable={false}
                          />
                          <Select
                            name={'ttsConfig.voiceName' as any}
                            control={control}
                            label="Voice Name"
                            options={getVoicesForLanguageAndType(formData.languageCode, formData.ttsConfig.voiceType).map((v) => ({
                              value: v.voice_name,
                              label: formatVoiceLabel(v),
                            }))}
                            isSearchable={true}
                            placeholder="Select voice..."
                          />
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Speaking Rate: {formData.ttsConfig.speakingRate}x
                            </label>
                            <input
                              type="range"
                              min="0.25"
                              max="4"
                              step="0.25"
                              value={formData.ttsConfig.speakingRate}
                              onChange={(e) => setValue('ttsConfig.speakingRate' as any, parseFloat(e.target.value))}
                              className="w-full accent-brand-purple-600"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Pitch: {!isVoiceTypePitchSupported(formData.ttsConfig.voiceType) ? 'N/A' : `${formData.ttsConfig.pitch > 0 ? '+' : ''}${formData.ttsConfig.pitch}`}
                            </label>
                            <input
                              type="range"
                              min="-20"
                              max="20"
                              step="1"
                              value={isVoiceTypePitchSupported(formData.ttsConfig.voiceType) ? formData.ttsConfig.pitch : 0}
                              onChange={(e) => setValue('ttsConfig.pitch' as any, parseInt(e.target.value))}
                              disabled={!isVoiceTypePitchSupported(formData.ttsConfig.voiceType)}
                              className={`w-full accent-brand-purple-600 ${!isVoiceTypePitchSupported(formData.ttsConfig.voiceType) ? 'opacity-40 cursor-not-allowed' : ''}`}
                              title={!isVoiceTypePitchSupported(formData.ttsConfig.voiceType) ? 'Pitch adjustment is not supported for this voice type' : ''}
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Volume Gain: {formData.ttsConfig.volumeGainDb > 0 ? '+' : ''}{formData.ttsConfig.volumeGainDb} dB
                            </label>
                            <input
                              type="range"
                              min="-20"
                              max="16"
                              step="2"
                              value={formData.ttsConfig.volumeGainDb}
                              onChange={(e) => setValue('ttsConfig.volumeGainDb' as any, parseInt(e.target.value))}
                              className="w-full accent-brand-purple-600"
                            />
                          </div>
                        </div>

                        {/* Voice Preview */}
                        <div className="pt-2 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const preview_btn = document.getElementById('voice-preview-btn');
                                if (preview_btn) preview_btn.setAttribute('disabled', 'true');
                                const response = await (await import('@/services/api')).default.post('/api/tts/preview', {
                                  text: getPreviewText(formData.languageCode),
                                  language_code: formData.languageCode,
                                  voice_name: formData.ttsConfig.voiceName,
                                  voice_type: formData.ttsConfig.voiceType,
                                  speaking_rate: formData.ttsConfig.speakingRate,
                                  ...(isVoiceTypePitchSupported(formData.ttsConfig.voiceType) ? { pitch: formData.ttsConfig.pitch } : {}),
                                  volume_gain_db: formData.ttsConfig.volumeGainDb,
                                });
                                const audio_data = response.data?.data?.audio_base64;
                                if (audio_data) {
                                  if (previewAudioRef.current) {
                                    previewAudioRef.current.pause();
                                    previewAudioRef.current.currentTime = 0;
                                  }
                                  const audio = new Audio(`data:audio/mp3;base64,${audio_data}`);
                                  previewAudioRef.current = audio;
                                  audio.play();
                                } else {
                                  toast.error('No audio data received');
                                }
                              } catch (err: any) {
                                toast.error(err?.response?.data?.description || 'Voice preview failed');
                              } finally {
                                const preview_btn = document.getElementById('voice-preview-btn');
                                if (preview_btn) preview_btn.removeAttribute('disabled');
                              }
                            }}
                            id="voice-preview-btn"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Volume2 className="w-3 h-3" />
                            Preview Voice
                          </button>
                        </div>
                      </div>
                      )}

                      {/* Caption Configuration - hidden for full_animated (always enabled) */}
                      {(formData.contentDeliveryType === 'video_presentation' || (formData.contentDeliveryType === 'avatar_video' && formData.avatarConfig?.videoMode !== 'full_animated_video')) && (
                      <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-primary-600" />
                            <h4 className="text-xs font-semibold text-gray-700">Video Captions</h4>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.captionConfig?.enableCaptions}
                              onChange={(e) => setValue('captionConfig.enableCaptions' as any, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                        <p className={`text-[10px] leading-relaxed ${formData.captionConfig?.enableCaptions ? 'text-gray-500' : 'text-gray-400'}`}>
                          ASS karaoke subtitles
                        </p>
                      </div>
                      )}

                      {/* HeyGen structure cap notice */}
                      {isHeygenFullAnimated && (
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          HeyGen avatar videos are limited to {HEYGEN_MAX_MODULES} modules and {HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE} content items per module (each is a separate long-running render).
                        </p>
                      )}

                      {/* Module Generation Config */}
                      <div className="space-y-2">
                        {isHeygenFullAnimated ? (
                          // Avatar (HeyGen): single value — sent as both min and max.
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Modules: {formData.moduleGenerationConfig.maxModules}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={HEYGEN_MAX_MODULES}
                              value={formData.moduleGenerationConfig.maxModules}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                setValue('moduleGenerationConfig.minModules', val);
                                setValue('moduleGenerationConfig.maxModules', val);
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                            <p className="text-xs text-gray-500">Range: 1-{HEYGEN_MAX_MODULES} (HeyGen limit)</p>
                          </div>
                        ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Min Modules: {formData.moduleGenerationConfig.minModules}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={10}
                              value={formData.moduleGenerationConfig.minModules}
                              onChange={(e) => {
                                const newMin = parseInt(e.target.value);
                                setValue('moduleGenerationConfig.minModules', newMin);
                              }}
                              onBlur={() => {
                                // Validate on blur
                                if (formData.moduleGenerationConfig.minModules > formData.moduleGenerationConfig.maxModules) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    moduleConfig: 'Min modules cannot be greater than max modules'
                                  }));
                                } else {
                                  setValidationErrors(prev => ({ ...prev, moduleConfig: undefined }));
                                }
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Max Modules: {formData.moduleGenerationConfig.maxModules}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={10}
                              value={formData.moduleGenerationConfig.maxModules}
                              onChange={(e) => {
                                const newMax = parseInt(e.target.value);
                                setValue('moduleGenerationConfig.maxModules', newMax);
                              }}
                              onBlur={() => {
                                // Validate on blur
                                if (formData.moduleGenerationConfig.minModules > formData.moduleGenerationConfig.maxModules) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    moduleConfig: 'Min modules cannot be greater than max modules'
                                  }));
                                } else {
                                  setValidationErrors(prev => ({ ...prev, moduleConfig: undefined }));
                                }
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                          </div>
                        </div>
                        )}
                        {validationErrors.moduleConfig && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {validationErrors.moduleConfig}
                          </p>
                        )}
                      </div>

                      {/* Content Generation Config */}
                      <div className="space-y-2">
                        {isHeygenFullAnimated ? (
                          // Avatar (HeyGen): single value — sent as both min and max.
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Content Items per Module: {formData.contentGenerationConfig.maxContentItemsPerModule}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE}
                              value={formData.contentGenerationConfig.maxContentItemsPerModule}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                setValue('contentGenerationConfig.minContentItemsPerModule', val);
                                setValue('contentGenerationConfig.maxContentItemsPerModule', val);
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                            <p className="text-xs text-gray-500">Range: 1-{HEYGEN_MAX_CONTENT_ITEMS_PER_MODULE} (HeyGen limit)</p>
                          </div>
                        ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Min Content Items per Module: {formData.contentGenerationConfig.minContentItemsPerModule}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={6}
                              value={formData.contentGenerationConfig.minContentItemsPerModule}
                              onChange={(e) => {
                                const newMin = parseInt(e.target.value);
                                setValue('contentGenerationConfig.minContentItemsPerModule', newMin);
                              }}
                              onBlur={() => {
                                // Validate on blur
                                if (formData.contentGenerationConfig.minContentItemsPerModule > formData.contentGenerationConfig.maxContentItemsPerModule) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    contentConfig: 'Min content items cannot be greater than max content items'
                                  }));
                                } else {
                                  setValidationErrors(prev => ({ ...prev, contentConfig: undefined }));
                                }
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                            <p className="text-xs text-gray-500">Range: 1-6 (Default: 3)</p>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Max Content Items per Module: {formData.contentGenerationConfig.maxContentItemsPerModule}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max={6}
                              value={formData.contentGenerationConfig.maxContentItemsPerModule}
                              onChange={(e) => {
                                const newMax = parseInt(e.target.value);
                                setValue('contentGenerationConfig.maxContentItemsPerModule', newMax);
                              }}
                              onBlur={() => {
                                // Validate on blur
                                if (formData.contentGenerationConfig.minContentItemsPerModule > formData.contentGenerationConfig.maxContentItemsPerModule) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    contentConfig: 'Min content items cannot be greater than max content items'
                                  }));
                                } else {
                                  setValidationErrors(prev => ({ ...prev, contentConfig: undefined }));
                                }
                              }}
                              className="w-full accent-brand-purple-600"
                            />
                            <p className="text-xs text-gray-500">Range: 1-6 (Default: 4)</p>
                          </div>
                        </div>
                        )}
                        {validationErrors.contentConfig && (
                          <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {validationErrors.contentConfig}
                          </p>
                        )}
                      </div>

                      {/* Scene Generation Config — hidden for full_animated (no scenes) */}
                      {(formData.contentDeliveryType === 'video_presentation' || (formData.contentDeliveryType === 'avatar_video' && formData.avatarConfig?.videoMode !== 'full_animated_video')) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Min Scenes per Content: {formData.sceneGenerationConfig.minScenesPerContent}
                          </label>
                          <input
                            type="range"
                            min="4"
                            max="10"
                            value={formData.sceneGenerationConfig.minScenesPerContent}
                            onChange={(e) => {
                              const newMin = parseInt(e.target.value);
                              setValue('sceneGenerationConfig.minScenesPerContent' as any, newMin);
                              setValue('sceneGenerationConfig.maxScenesPerContent' as any, Math.max(newMin, formData.sceneGenerationConfig.maxScenesPerContent));
                            }}
                            className="w-full accent-brand-purple-600"
                          />
                          <p className="text-xs text-gray-500">Range: 4-10 (Default: 4)</p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-600">
                            Max Scenes per Content: {formData.sceneGenerationConfig.maxScenesPerContent}
                          </label>
                          <input
                            type="range"
                            min="4"
                            max="10"
                            value={formData.sceneGenerationConfig.maxScenesPerContent}
                            onChange={(e) => {
                              const newMax = parseInt(e.target.value);
                              setValue('sceneGenerationConfig.maxScenesPerContent' as any, newMax);
                              setValue('sceneGenerationConfig.minScenesPerContent' as any, Math.min(newMax, formData.sceneGenerationConfig.minScenesPerContent));
                            }}
                            className="w-full accent-brand-purple-600"
                          />
                          <p className="text-xs text-gray-500">Range: 4-10 (Default: 6)</p>
                        </div>
                      </div>
                      )}

                      {/* HTML Slides Configuration - commented out: content_format not consumed by backend */}
                      {/* <div className="p-3 bg-gray-50 rounded-lg space-y-3">
                        <h4 className="text-xs font-semibold text-gray-700">HTML Slides Configuration</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Content Format</label>
                            <select
                              value={formData.htmlSlidesConfig.contentFormat}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  htmlSlidesConfig: { ...formData.htmlSlidesConfig, contentFormat: e.target.value as any }
                                });
                              }}
                              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="html">HTML Content</option>
                              <option value="plain">Plain Text</option>
                              <option value="audio-friendly">Audio-Friendly</option>
                              <option value="html-slides">HTML Slides</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Theme
                              <span className="ml-2 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white text-[8px] px-1 py-0.5 rounded-full font-bold">
                                PRO
                              </span>
                            </label>
                            <select
                              value={formData.htmlSlidesConfig.theme}
                              disabled
                              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed opacity-60"
                              title="Pro feature - Theme customization is available in the Pro plan"
                            >
                              <option value="educational">Educational</option>
                              <option value="modern" disabled>Modern (PRO)</option>
                              <option value="corporate" disabled>Corporate (PRO)</option>
                              <option value="dark" disabled>Dark (PRO)</option>
                              <option value="minimal" disabled>Minimal (PRO)</option>
                            </select>
                          </div>
                        </div>
                        {formData.htmlSlidesConfig.contentFormat === 'html-slides' && (
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-gray-600">Slide Content Types</label>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { key: 'html', enabled: true },
                                { key: 'video', enabled: true },
                                { key: 'audio', enabled: true },
                                { key: 'interactive', enabled: false },
                                { key: 'exercise', enabled: false },
                                { key: 'demo', enabled: false }
                              ].map(({ key: type, enabled }) => (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => enabled && toggleHtmlSlidesContentType(type)}
                                  disabled={!enabled}
                                  className={`px-2 py-1 text-xs rounded-md border transition-colors relative ${
                                    !enabled
                                      ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                                      : formData.htmlSlidesConfig.contentTypes.includes(type)
                                      ? 'bg-brand-blue-50 border-brand-blue-300 text-brand-blue-700'
                                      : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                  }`}
                                  title={!enabled ? 'Pro feature - This content type is available in the Pro plan' : ''}
                                >
                                  {type}
                                  {!enabled && (
                                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white text-[8px] px-1 rounded-full font-bold">
                                      PRO
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div> */}
                    </div>
                  </div>

                  {/* Enhanced Features - Pro Only */}
                  {/* <div className="space-y-4 p-4 bg-white rounded-xl border border-gray-200 opacity-60">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-600" />
                        Enhanced Pedagogical Features
                        <span className="ml-2 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white text-[8px] px-1 py-0.5 rounded-full font-bold">
                          PRO
                        </span>
                      </h3>
                      <label className="relative inline-flex items-center cursor-not-allowed" title="Pro feature - Enhanced pedagogical features are available in the Pro plan">
                        <input
                          type="checkbox"
                          checked={false}
                          disabled
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 rounded-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5"></div>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                      {[
                        { key: 'autoGrading', label: 'Auto-Grading', icon: Award, desc: 'Automatic exercise scoring' },
                        { key: 'selfCheckQuizzes', label: 'Self-Check Quizzes', icon: RefreshCw, desc: 'Adaptive self-assessment' },
                        { key: 'adaptiveLearning', label: 'Adaptive Learning', icon: Brain, desc: 'Personalized difficulty' },
                        { key: 'analytics', label: 'Learning Analytics', icon: BarChart3, desc: 'Progress tracking' },
                        { key: 'collaborative', label: 'Collaborative Features', icon: MessageSquare, desc: 'Discussion & peer review' },
                        { key: 'microlearning', label: 'Microlearning', icon: Timer, desc: 'Bite-sized modules' }
                      ].map(({ key, label, icon: Icon, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 cursor-not-allowed" title="Pro feature - Available in Pro plan">
                          <input
                            type="checkbox"
                            checked={false}
                            disabled
                            className="mt-1 rounded border-gray-300 text-gray-400 cursor-not-allowed"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-400">{label}</span>
                              <span className="bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white text-[8px] px-1 rounded-full font-bold">
                                PRO
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div> */}

                  {/* Comprehensive Assessment Configuration */}
                  <div className="space-y-3 p-3 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                        <Clipboard className="w-4 h-4 text-brand-green-600" />
                        Assessment Configuration
                      </h3>
                      <Toggle
                        name="assessmentConfig.enabled"
                        control={control}
                        onChange={(checked) => {
                          // Clear assessment errors when disabled
                          if (!checked) {
                            setValidationErrors(prev => ({ ...prev, assessmentConfig: undefined }));
                          }
                        }}
                      />
                    </div>

                    {watch('assessmentConfig.enabled') && (
                      <div className="space-y-3 pt-2">
                        {/* Question Types */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Question Types <span className="text-red-500">*</span>
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'TRUE_FALSE', 'TEXT_ANSWER'].map((type) => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => {
                                  const types = watch('assessmentConfig.questionTypes');
                                  const newTypes = types.includes(type as any)
                                    ? types.filter(t => t !== type)
                                    : [...types, type as any];
                                  setValue('assessmentConfig.questionTypes', newTypes);
                                }}
                                className={`px-2 py-1 text-xs rounded border transition-colors ${
                                  watch('assessmentConfig.questionTypes').includes(type as any)
                                    ? 'bg-primary-600 border-primary-600 text-white'
                                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                              >
                                {type.replace('_', ' ')}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Assessment Levels */}
                        <div className="grid grid-cols-1 gap-2">
                          {/* Content Item Assessments */}
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">Content Item Level</span>
                              <div className="flex gap-3 text-xs items-center">
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.contentItemAssessments.generatePre"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Pre</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.contentItemAssessments.generatePost"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Post</span>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Questions</label>
                                <div className="flex gap-1 items-center">
                                  <Input
                                    name="assessmentConfig.contentItemAssessments.minQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Min questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 10, message: 'Max 10 questions' }
                                    }}
                                    error={errors.assessmentConfig?.contentItemAssessments?.minQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                  <span className="text-xs text-gray-400">-</span>
                                  <Input
                                    name="assessmentConfig.contentItemAssessments.maxQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Max questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 10, message: 'Max 10 questions' }
                                    }}
                                    error={errors.assessmentConfig?.contentItemAssessments?.maxQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                </div>
                              </div>
                              <div>
                                <Input
                                  name="assessmentConfig.contentItemAssessments.passingScore"
                                  control={control}
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  label="Pass %"
                                  rules={{
                                    required: 'Passing score required',
                                    min: { value: 1, message: 'Min 1%' },
                                    max: { value: 100, message: 'Max 100%' }
                                  }}
                                  error={errors.assessmentConfig?.contentItemAssessments?.passingScore?.message}
                                  className="!w-16 !px-2 !py-1.5 !text-xs"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Module Assessments */}
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">Module Level</span>
                              <div className="flex gap-3 text-xs items-center">
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.moduleAssessments.generatePre"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Pre</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.moduleAssessments.generatePost"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Post</span>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Questions</label>
                                <div className="flex gap-1 items-center">
                                  <Input
                                    name="assessmentConfig.moduleAssessments.minQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Min questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 25, message: 'Max 25 questions' }
                                    }}
                                    error={errors.assessmentConfig?.moduleAssessments?.minQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                  <span className="text-xs text-gray-400">-</span>
                                  <Input
                                    name="assessmentConfig.moduleAssessments.maxQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Max questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 25, message: 'Max 25 questions' }
                                    }}
                                    error={errors.assessmentConfig?.moduleAssessments?.maxQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                </div>
                              </div>
                              <div>
                                <Input
                                  name="assessmentConfig.moduleAssessments.passingScore"
                                  control={control}
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  label="Pass %"
                                  rules={{
                                    required: 'Passing score required',
                                    min: { value: 1, message: 'Min 1%' },
                                    max: { value: 100, message: 'Max 100%' }
                                  }}
                                  error={errors.assessmentConfig?.moduleAssessments?.passingScore?.message}
                                  className="!w-16 !px-2 !py-1.5 !text-xs"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Course Assessments */}
                          <div className="p-2 bg-gray-50 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">Course Level</span>
                              <div className="flex gap-3 text-xs items-center">
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.courseAssessments.generatePre"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Pre</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Toggle
                                    name="assessmentConfig.courseAssessments.generatePost"
                                    control={control}
                                  />
                                  <span className="text-gray-700">Post</span>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Questions</label>
                                <div className="flex gap-1 items-center">
                                  <Input
                                    name="assessmentConfig.courseAssessments.minQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Min questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 50, message: 'Max 50 questions' }
                                    }}
                                    error={errors.assessmentConfig?.courseAssessments?.minQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                  <span className="text-xs text-gray-400">-</span>
                                  <Input
                                    name="assessmentConfig.courseAssessments.maxQuestions"
                                    control={control}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    rules={{
                                      required: 'Max questions required',
                                      min: { value: 1, message: 'Min 1 question' },
                                      max: { value: 50, message: 'Max 50 questions' }
                                    }}
                                    error={errors.assessmentConfig?.courseAssessments?.maxQuestions?.message}
                                    className="!w-14 !px-2 !py-1.5 !text-xs"
                                  />
                                </div>
                              </div>
                              <div>
                                <Input
                                  name="assessmentConfig.courseAssessments.passingScore"
                                  control={control}
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  label="Pass %"
                                  rules={{
                                    required: 'Passing score required',
                                    min: { value: 1, message: 'Min 1%' },
                                    max: { value: 100, message: 'Max 100%' }
                                  }}
                                  error={errors.assessmentConfig?.courseAssessments?.passingScore?.message}
                                  className="!w-16 !px-2 !py-1.5 !text-xs"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Common Settings */}
                        <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-gray-200">
                          <Toggle
                            name="assessmentConfig.allowRetry"
                            control={control}
                            label="Allow Retry"
                          />
                          <Toggle
                            name="assessmentConfig.shuffleOptions"
                            control={control}
                            label="Shuffle Options"
                          />
                          <Toggle
                            name="assessmentConfig.immediateFeedback"
                            control={control}
                            label="Immediate Feedback"
                          />
                          <Toggle
                            name="assessmentConfig.showCorrectAnswers"
                            control={control}
                            label="Show Answers"
                          />
                        </div>

                        {/* Validation Error Display */}
                        {validationErrors.assessmentConfig && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-xs flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 flex-shrink-0" />
                              <span>{validationErrors.assessmentConfig}</span>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* Enhanced Action Section */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gray-50 border-t border-gray-100 rounded-b-xl sm:rounded-b-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="order-2 sm:order-1" />
              <button
                type="submit"
                disabled={!canWrite || !formData.courseIdea.trim() || formData.courseIdea.trim().length < 100 || isGenerating || (formData.contentDeliveryType === 'avatar_video' && !formData.avatarConfig.avatarId)}
                title={
                  !canWrite
                    ? 'You do not have permission to generate courses'
                    : (formData.contentDeliveryType === 'avatar_video' && !formData.avatarConfig.avatarId)
                      ? 'Select an avatar before generating the avatar video'
                      : undefined
                }
                className="relative w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-gradient-to-r from-primary-600 to-brand-purple-600 text-white font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl hover:from-primary-700 hover:to-brand-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 disabled:transform-none order-1 sm:order-2"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2 sm:gap-3">
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span className="text-sm sm:text-base">Generating...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2 sm:gap-3">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-sm sm:text-base">Generate Course</span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Enhanced Recent Generations Section */}
        {isLoadingHistory ? (
          <div className="mt-8 sm:mt-12 lg:mt-16">
            <div className="text-center mb-4 sm:mb-6 lg:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Recent Course Generations</h2>
              <p className="text-sm sm:text-base text-gray-600">Loading your previous courses...</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {[1, 2, 3].map((index) => (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 pr-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                    <div className="h-6 w-16 bg-gray-200 rounded-full" />
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-4" />
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-4">
                      <div className="h-3 w-16 bg-gray-200 rounded" />
                      <div className="h-3 w-14 bg-gray-200 rounded" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                      <div className="h-3 w-8 bg-gray-200 rounded" />
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="text-center">
                          <div className="w-full h-3 bg-gray-200 rounded-full" />
                          <div className="mt-1 h-3 w-4 bg-gray-200 rounded mx-auto" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : orchestrationHistory.length > 0 && (
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Recent Course Generations</h2>
              <p className="text-gray-600">Continue working on your previously created courses</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {orchestrationHistory.map((orchestration) => (
                <div
                  key={orchestration._id}
                  className="group relative bg-white rounded-2xl border border-gray-100 hover:shadow-xl hover:border-primary-200 transition-all duration-300 cursor-pointer transform hover:-translate-y-1 flex flex-col h-[320px]"
                  onClick={() => navigate(`/admin/course/build-course?orchestrationId=${orchestration._id}`)}
                >
                  {/* Card Content */}
                  <div className="p-6 pb-0 flex-1 flex flex-col">
                    {/* Title */}
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-900 line-clamp-2 text-base leading-tight">
                        {orchestration.user_course_idea || 'Untitled Course'}
                      </h3>
                    </div>

                    {/* Status and Time Row */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(orchestration.createdAt)}
                      </p>
                      <div className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border ${getStatusColor(orchestration)}`}>
                        {getStatusIcon(orchestration)}
                        <span className="capitalize">{getStatusText(orchestration)}</span>
                      </div>
                    </div>

                    {/* Description - 2 lines only */}
                    <div className="flex-1">
                      {orchestration.short_description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                          {orchestration.short_description}
                        </p>
                      )}
                    </div>

                    {/* Course Stats */}
                    <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        {orchestration.total_modules > 0 && (
                          <span className="flex items-center gap-1.5">
                            <BookOpen className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{orchestration.total_modules}</span>
                            <span>modules</span>
                          </span>
                        )}
                        {orchestration.total_content_items > 0 && (
                          <span className="flex items-center gap-1.5">
                            <div className="w-4 h-4 rounded bg-gray-200 flex items-center justify-center">
                              <div className="w-2 h-2 bg-gray-400 rounded-full" />
                            </div>
                            <span className="font-medium">{orchestration.total_content_items}</span>
                            <span>items</span>
                          </span>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all duration-200" />
                    </div>
                  </div>

                  {/* Fixed Bottom Progress Bar Section */}
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                      <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                        <span>Generation Progress</span>
                        <span>
                          {[
                            orchestration.metadata_generation_status?.status,
                            orchestration.image_generation_status?.status,
                            orchestration.course_skeleton_generation_status?.status,
                            orchestration.module_content_expansion_status?.status,
                            // Validation stage only exists on newer courses (older ones have no node).
                            ...(hasValidationStage(orchestration) ? [orchestration.course_validation_status?.status] : [])
                          ].filter(status => status?.toLowerCase() === 'completed').length}/{hasValidationStage(orchestration) ? 5 : 4}
                        </span>
                      </div>
                      
                      <div className={`grid ${hasValidationStage(orchestration) ? 'grid-cols-5' : 'grid-cols-4'} gap-1`}>
                        {[
                          { status: orchestration.metadata_generation_status?.status, label: 'Meta', icon: 'FileText' },
                          { status: orchestration.image_generation_status?.status, label: 'Images', icon: 'Image' },
                          { status: orchestration.course_skeleton_generation_status?.status, label: 'Skeleton', icon: 'BookOpen' },
                          { status: orchestration.module_content_expansion_status?.status, label: 'Content', icon: 'FileText' },
                          // Validation stage — only rendered for newer courses that have the node.
                          ...(hasValidationStage(orchestration)
                            ? [{ status: orchestration.course_validation_status?.status, label: 'Validation', icon: 'ShieldCheck' }]
                            : [])
                        ].map((item, index) => {
                          const IconComponent =
                            item.icon === 'FileText' ? FileText :
                            item.icon === 'BookOpen' ? BookOpen :
                            item.icon === 'ShieldCheck' ? ShieldCheck :
                            item.icon === 'Image' ? Image : FileText;
                          
                          return (
                            <div key={index} className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <IconComponent className="w-4 h-4 text-gray-500 mb-1" />
                                <div 
                                  className={`w-full h-2 rounded-full transition-all duration-300 ${
                                    item.status?.toLowerCase() === 'completed' 
                                      ? 'bg-gradient-to-r from-green-400 to-green-500 shadow-sm' 
                                      : item.status?.toLowerCase() === 'failed'
                                      ? 'bg-gradient-to-r from-red-400 to-red-500'
                                      : item.status?.toLowerCase() === 'in_progress' || item.status?.toLowerCase() === 'processing'
                                      ? 'bg-gradient-to-r from-blue-400 to-blue-500 animate-pulse'
                                      : 'bg-gray-200'
                                  }`}
                                  title={item.label}
                                />
                                <span className="text-[10px] text-gray-400 mt-0.5">{item.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {orchestrationHistory.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Pagination
                  currentPage={historyPage}
                  totalPages={totalPages || 1}
                  totalItems={totalHistory || orchestrationHistory.length}
                  itemsPerPage={historyItemsPerPage}
                  onPageChange={setHistoryPage}
                  onItemsPerPageChange={handleHistoryItemsPerPageChange}
                  showItemsPerPage={true}
                  itemsPerPageOptions={[6, 12, 18, 24]}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Avatar Variations Modal */}
      {looksModalGroup && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setLooksModalGroup(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {looksModalGroup.name} — Variations
                </h3>
                <p className="text-[10px] text-gray-500">
                  Pick a specific outfit/pose for this avatar
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLooksModalGroup(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingLooks ? (
                <div className="grid grid-cols-4 gap-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : looksInModal.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {looksInModal.map((look) => (
                    <button
                      key={look.id}
                      type="button"
                      onClick={() => selectLook(look)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                        formData.avatarConfig.avatarId === look.id
                          ? 'border-brand-purple-400 ring-2 ring-brand-purple-200 bg-brand-purple-50'
                          : 'border-gray-200 hover:border-brand-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-full aspect-square rounded-md overflow-hidden bg-gray-100">
                        {look.preview_image_url ? (
                          <img
                            src={look.preview_image_url}
                            alt={look.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-600 truncate w-full text-center">{look.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-xs text-gray-400">
                  No variations available for this avatar
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Asks for confirmation when a knowledge store is selected but its suggested
          structure was never applied — the sliders are then the admin's own guess. */}
      {showAiWarning && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowAiWarning(false)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 border-b border-gray-100 bg-amber-50 px-5 py-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">
                  Generate without the suggested size?
                </h3>
                <p className="text-xs text-gray-600">
                  Your course size does not match what your documents were analysed to support.
                </p>
              </div>
            </div>

            <div className="space-y-3 px-5 py-4">
              {/* What is actually at stake, stated in both directions — a size below the
                  suggestion leaves material out, a size above it pads thin material. */}
              <p className="text-sm leading-relaxed text-gray-700">
                We read the documents in this knowledge store and suggested a size that matches
                the material they contain.
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  <span>A smaller course leaves parts of your documents untaught.</span>
                </li>
                <li className="flex gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  <span>A larger one stretches the same material thinner across more lessons.</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500">
                You can go back and select <span className="font-medium text-gray-700">Use this range</span>,
                or continue with the size you have set.
              </p>
            </div>

            <div className="flex gap-3 border-t border-gray-100 bg-gray-50 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowAiWarning(false)}
                className="flex-1 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-100"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={() => {
                  const pending = pendingGenerationRef.current;
                  setShowAiWarning(false);
                  if (!pending) return;
                  pendingGenerationRef.current = null;
                  setIsGenerating(true);
                  generateMutation.mutate(pending);
                }}
                className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
              >
                Generate anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
